import React, { useState, useEffect } from 'react';
import { useAppSelector } from '../../app/hooks';
import Select from 'react-select';
import fetchAPI from '../../common/fetchAPI';
import ExtensionTimeEntryPopover from './ExtensionTimeEntryPopover';
import { ShowToast } from '../../common/Toastr';
import moment from 'moment';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { validateCultureNumber, formatNumberCustomoriginal, dateFormatByAccountCulture } from '../../common/timeLiveFunction';

declare const chrome: any;

interface ExtensionManualEntryProps {
  onBackToTimer: () => void;
}

const formatOptionWithIcon = (option: any, iconClass: string, context: 'menu' | 'value') => {
  if (context === 'value') {
    return (
      <div className="d-flex align-items-center">
        <i className={`bi ${iconClass} me-3 text-gray-700 fs-6`}></i>
         <span className="fs-7 extensionHeading" style={{fontWeight: 'bold' }}>{option.label}</span>
        {/* <span className="fw-bold fs-7 fontWeight: 'bold'" style={{ color: '#181C32' }}>
          {option.label}
        </span> */}
      </div>
    );
  }
  return option.label;
};

const cleanDropdownStyles = {
  control: (base: any, state: any) => ({
    ...base,
    minHeight: '38px',
    height: '38px',
    borderRadius: '8px',
    borderColor: state.isFocused ? '#1B64F2' : '#dddddd',
    boxShadow: 'none',
    backgroundColor: state.isDisabled ? '#F9FAFB' : '#FFFFFF',
    '&:hover': {
      borderColor: '#1B64F2',
    },
  }),
  valueContainer: (base: any) => ({
    ...base,
    padding: '0 8px',
    height: '38px',
    display: 'flex',
    alignItems: 'center',
  }),
  input: (base: any) => ({
    ...base,
    margin: 0,
    padding: 0,
    fontSize: '13px',
  }),
  singleValue: (base: any) => ({
    ...base,
    fontSize: '13px',
    color: '#181C32', 
    margin: 0,
    display: 'flex',
    alignItems: 'center',
  }),
  placeholder: (base: any) => ({
    ...base,
    fontSize: '13px',
    color: '#A1A5B7',
  }),
  option: (base: any, state: any) => ({
    ...base,
    backgroundColor: state.isSelected 
      ? '#1B64F2' 
      : state.isFocused 
        ? '#F4F6F9' 
        : '#FFFFFF',
    color: state.isSelected ? '#FFFFFF' : '#181C32',
    fontSize: '13px',
    cursor: 'pointer',
    padding: '8px 12px',
    fontWeight: state.isSelected ? '500' : '400',
  }),
  menu: (base: any) => ({
    ...base,
    borderRadius: '8px',
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.08)',
    zIndex: 9999,
    overflow: 'hidden',
  }),
  menuList: (base: any) => ({
    ...base,
    padding: 0,
  }),
};

export const ExtensionManualEntry: React.FC<ExtensionManualEntryProps> = ({ onBackToTimer }) => {
  const {
    sessionAccountEmployeeId,
    timeZoneId,
    showClientInTimesheet,
    showCostCenterInTimeSheet,
    showWorkTypeInTimeSheet,
    IsTimeEntryDescriptionRequired,
    showClockStartEnd,
    decimalFormat,
    decimalPlaces,
    cultureInfoName,
    CurrentCultureDateFormat,
    customFieldInsertionFormat,
    calendarStartDay,
  } = useAppSelector((state) => state.globalState);

  const { defaultWorkTypeId } = useAppSelector((state: any) => state.timesheet || {});

  const getCurrentFormattedTime = (offsetHours = 0): string => {
    const now = new Date();
    now.setHours(now.getHours() + offsetHours);
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const showClientFlag: boolean = Boolean(showClientInTimesheet);
  const showCostCenterFlag: boolean = Boolean(showCostCenterInTimeSheet);
  const showWorkTypeFlag: boolean = Boolean(showWorkTypeInTimeSheet);

  const isClockPopoverActive: boolean = Boolean(showClockStartEnd) && !Boolean(decimalFormat);

  const [activeEmpId, setActiveEmpId] = useState<number | string | null>(null);
  const [isClientVisible, setIsClientVisible] = useState<boolean>(showClientFlag);
  const [isCostCenterVisible, setIsCostCenterVisible] = useState<boolean>(showCostCenterFlag);
  const [isWorkTypeVisible, setIsWorkTypeVisible] = useState<boolean>(showWorkTypeFlag);

  const [isStorageLoaded, setIsStorageLoaded] = useState<boolean>(false);

  const [clientOptions, setClientOptions] = useState<{ value: any; label: string }[]>([]);
  const [projectOptions, setProjectOptions] = useState<{ value: any; label: string }[]>([]);
  const [taskOptions, setTaskOptions] = useState<{ value: any; label: string }[]>([]);
  const [costCenterOptions, setCostCenterOptions] = useState<{ value: any; label: string }[]>([]);
  const [workTypeOptions, setWorkTypeOptions] = useState<{ value: any; label: string }[]>([]);

  const [selectedClient, setSelectedClient] = useState<any>(null);
  const [selectedProject, setSelectedProject] = useState<any>(null);
  const [selectedTask, setSelectedTask] = useState<any>(null);
  const [selectedCostCenter, setSelectedCostCenter] = useState<any>(null);
  const [selectedWorkType, setSelectedWorkType] = useState<any>(null);

  const [timeError, setTimeError] = useState<string>('');
  const [descriptionError, setDescriptionError] = useState<string>('');
  const [customFieldError, setCustomFieldError] = useState<string>('');

  const [savedIds, setSavedIds] = useState<{
    clientId?: any;
    projectId?: any;
    taskId?: any;
    costCenterId?: any;
    workTypeId?: any;
  }>({});

  const [loadingClients, setLoadingClients] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [loadingCostCenters, setLoadingCostCenters] = useState(false);
  const [loadingWorkTypes, setLoadingWorkTypes] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [entryDate, setEntryDate] = useState<string>(new Date().toISOString().split('T')[0]);
  
  const [hours, setHours] = useState<string>('');
  const [startTime, setStartTime] = useState<string>(() => getCurrentFormattedTime(0));
  const [endTime, setEndTime] = useState<string>(() => getCurrentFormattedTime(0));
  const [showTimePopover, setShowTimePopover] = useState<boolean>(false);
  const [description, setDescription] = useState<string>('');
  const [isDescriptionRequired, setIsDescriptionRequired] = useState<boolean>(Boolean(IsTimeEntryDescriptionRequired));

  // Custom Fields States
  const [customFieldsTemplate, setCustomFieldsTemplate] = useState<any[]>([]);
  const [cfValues, setCfValues] = useState<any>({});

  // useEffect(() => {
  //   if (!isStorageLoaded) return;

  //   if (typeof chrome !== 'undefined' && chrome.storage) {
  //     chrome.storage.local.get(['customFieldsTemplate'], (result:any) => {
  //       if (result?.customFieldsTemplate && result.customFieldsTemplate.length > 0) {
  //         setCustomFieldsTemplate(result.customFieldsTemplate);
  //         initializeCfValues(result.customFieldsTemplate);
  //       } else {
  //         fetchCustomFieldsFromAPI();
  //       }
  //     });
  //   } else {
  //     fetchCustomFieldsFromAPI();
  //   }
  // }, [isStorageLoaded]);
  useEffect(() => {
    if (!isStorageLoaded) return;
    fetchCustomFieldsFromAPI();
  }, [isStorageLoaded]);

  const fetchCustomFieldsFromAPI = () => {
    fetchAPI('api/Timesheets/GetCustomFieldTemplate', 'GET', '', true)
      .then((res) => res.json())
      .then((data) => {
        const list = Array.isArray(data) ? data : data?.data || [];
        setCustomFieldsTemplate(list);
        initializeCfValues(list);
        
        if (typeof chrome !== 'undefined' && chrome.storage) {
          chrome.storage.local.set({ customFieldsTemplate: list });
        }
      })
      .catch((err) => console.error('Error fetching custom fields:', err));
  };

  const initializeCfValues = (templateList: any[]) => {
    let initialCfVals: any = {};
    templateList.filter((x: any) => !x.IsDisabled).forEach((x: any) => {
      if (x.MasterCustomDataTypeName === "Date") {
        initialCfVals[x.DatabaseFieldName] = moment(new Date()).format(customFieldInsertionFormat || 'YYYY-MM-DD');
      } else {
        initialCfVals[x.DatabaseFieldName] = x.DefaultValue || "";
      }
    });
    setCfValues(initialCfVals);
  };

  const handleCustomFieldChange = (e: any) => {
    setCfValues({ ...cfValues, [e.target.name]: e.target.value });
    if (customFieldError) setCustomFieldError('');
  };

  const handleCustomFieldDatePicker = (date: any, name: string) => {
    const formattedDate = date ? moment(date).format(customFieldInsertionFormat || 'YYYY-MM-DD') : moment().format(customFieldInsertionFormat || 'YYYY-MM-DD');
    setCfValues({ ...cfValues, [name]: formattedDate });
    if (customFieldError) setCustomFieldError('');
  };

  const persistStorage = (dataToSave: Record<string, any>) => {
    if (typeof chrome !== 'undefined' && chrome.storage) {
      chrome.storage.local.set(dataToSave);
    } else {
      Object.entries(dataToSave).forEach(([k, v]) => {
        if (v === null || v === undefined) localStorage.removeItem(k);
        else localStorage.setItem(k, JSON.stringify(v));
      });
    }
  };

  useEffect(() => {
    const keysToFetch = [
      'sessionAccountEmployeeId',
      'accountEmployeeId',
      'showClientInTimesheet',
      'showCostCenterInTimeSheet',
      'showWorkTypeInTimeSheet',
      'manual_clientId',
      'manual_projectId',
      'manual_taskId',
      'manual_costCenterId',
      'manual_workTypeId',
      'IsTimeEntryDescriptionRequired',
    ];

    if (typeof chrome !== 'undefined' && chrome.storage) {
      chrome.storage.local.get(keysToFetch, (result: any) => {
        const resolvedEmpId = result?.accountEmployeeId || result?.sessionAccountEmployeeId || sessionAccountEmployeeId;
        if (resolvedEmpId) setActiveEmpId(Number(resolvedEmpId));

        if (result?.showClientInTimesheet !== undefined) setIsClientVisible(Boolean(result.showClientInTimesheet));
        if (result?.showCostCenterInTimeSheet !== undefined) setIsCostCenterVisible(Boolean(result.showCostCenterInTimeSheet));
        if (result?.showWorkTypeInTimeSheet !== undefined) setIsWorkTypeVisible(Boolean(result.showWorkTypeInTimeSheet));
        if (result?.IsTimeEntryDescriptionRequired !== undefined) {
          setIsDescriptionRequired(Boolean(result.IsTimeEntryDescriptionRequired));
        } else if (IsTimeEntryDescriptionRequired !== undefined && IsTimeEntryDescriptionRequired !== null) {
          setIsDescriptionRequired(Boolean(IsTimeEntryDescriptionRequired));
          persistStorage({ IsTimeEntryDescriptionRequired: Boolean(IsTimeEntryDescriptionRequired) });
        }

        setSavedIds({
          clientId: result?.manual_clientId ?? null,
          projectId: result?.manual_projectId ?? null,
          taskId: result?.manual_taskId ?? null,
          costCenterId: result?.manual_costCenterId ?? null,
          workTypeId: result?.manual_workTypeId ?? null,
        });

        setIsStorageLoaded(true);
      });
    } else {
      if (sessionAccountEmployeeId) setActiveEmpId(Number(sessionAccountEmployeeId));
      setIsDescriptionRequired(Boolean(IsTimeEntryDescriptionRequired));
      const parseLocal = (k: string) => {
        const val = localStorage.getItem(k);
        return val ? JSON.parse(val) : null;
      };
      setSavedIds({
        clientId: parseLocal('manual_clientId'),
        projectId: parseLocal('manual_projectId'),
        taskId: parseLocal('manual_taskId'),
        costCenterId: parseLocal('manual_costCenterId'),
        workTypeId: parseLocal('manual_workTypeId'),
      });
      setIsStorageLoaded(true);
    }
  }, [sessionAccountEmployeeId, IsTimeEntryDescriptionRequired]);

  const handleClearAll = () => {
    setSelectedClient(null);
    setSelectedProject(null);
    setSelectedTask(null);
    setSelectedCostCenter(null);
    setSelectedWorkType(null);
    setHours('');
    setTimeError('');
    setDescription('');
    setSavedIds({});
    setDescriptionError('');
    setCustomFieldError('');

    persistStorage({
      manual_clientId: null,
      manual_projectId: null,
      manual_taskId: null,
      manual_costCenterId: null,
      manual_workTypeId: null,
    });
  };

  const handleClientChange = (opt: any) => {
    setSelectedClient(opt);
    const val = opt?.value ?? null;
    setSavedIds((prev) => ({ ...prev, clientId: val, projectId: null, taskId: null, costCenterId: null }));
    persistStorage({
      manual_clientId: val,
      manual_projectId: null,
      manual_taskId: null,
      manual_costCenterId: null,
    });

    if (!opt) {
      setSelectedProject(null);
      setSelectedTask(null);
      setSelectedCostCenter(null);
    }
  };

  const handleProjectChange = (opt: any) => {
    setSelectedProject(opt);
    const val = opt?.value ?? null;
    setSavedIds((prev) => ({ ...prev, projectId: val, taskId: null, costCenterId: null }));
    persistStorage({
      manual_projectId: val,
      manual_taskId: null,
      manual_costCenterId: null,
    });

    if (!opt) {
      setSelectedTask(null);
      setSelectedCostCenter(null);
    }
  };

  const handleTaskChange = (opt: any) => {
    setSelectedTask(opt);
    const val = opt?.value ?? null;
    setSavedIds((prev) => ({ ...prev, taskId: val, costCenterId: null }));
    persistStorage({
      manual_taskId: val,
      manual_costCenterId: null,
    });

    if (!opt) {
      setSelectedCostCenter(null);
    }
  };

  const handleCostCenterChange = (opt: any) => {
    setSelectedCostCenter(opt);
    const val = opt?.value ?? null;
    setSavedIds((prev) => ({ ...prev, costCenterId: val }));
    persistStorage({ manual_costCenterId: val });
  };

  const handleWorkTypeChange = (opt: any) => {
    setSelectedWorkType(opt);
    const val = opt?.value ?? null;
    setSavedIds((prev) => ({ ...prev, workTypeId: val }));
    persistStorage({ manual_workTypeId: val });
  };

  useEffect(() => {
    if (!isStorageLoaded || !activeEmpId) return;

    if (isClientVisible) {
      setLoadingClients(true);
      fetchAPI(`api/Timesheets/GetAccountClientForTimesheet/${activeEmpId}/0`, 'GET', '', true)
        .then((res) => res.json())
        .then((data) => {
          const list = Array.isArray(data) ? data : data?.data || [];
          const formatted = list.map((item: any) => ({
            value: item.AccountClientId,
            label: item.ClientName,
          }));
          setClientOptions(formatted);

          const targetId = savedIds.clientId;
          if (targetId) {
            const match = formatted.find((c: any) => Number(c.value) === Number(targetId));
            if (match) setSelectedClient(match);
          }
        })
        .catch((err) => console.error('Error fetching clients:', err))
        .finally(() => setLoadingClients(false));
    } else {
      setLoadingProjects(true);
      fetchAPI(`api/Projects/GetAccountProjectsForTimesheet/0/${activeEmpId}`, 'GET', '', true)
        .then((res) => res.json())
        .then((data) => {
          const list = Array.isArray(data) ? data : data?.data || [];
          const formatted = list.map((item: any) => ({
            value: item.AccountProjectId,
            label: item.ProjectName,
          }));
          setProjectOptions(formatted);

          const targetId = savedIds.projectId;
          if (targetId) {
            const match = formatted.find((p: any) => Number(p.value) === Number(targetId));
            if (match) setSelectedProject(match);
          }
        })
        .catch((err) => console.error('Error fetching projects:', err))
        .finally(() => setLoadingProjects(false));
    }

    if (isWorkTypeVisible) {
      setLoadingWorkTypes(true);
      fetchAPI('api/WorkTypes', 'GET', '', true)
        .then((res) => res.json())
        .then((data) => {
          const list = Array.isArray(data) ? data : data?.data || [];
          const formatted = list.map((item: any) => ({
            value: item.AccountWorkTypeId,
            label: item.AccountWorkType,
          }));
          setWorkTypeOptions(formatted);

          const targetId = savedIds.workTypeId || defaultWorkTypeId;
          if (targetId) {
            const match = formatted.find((w: any) => Number(w.value) === Number(targetId));
            if (match) setSelectedWorkType(match);
          } else if (formatted.length > 0) {
            setSelectedWorkType(formatted[0]);
          }
        })
        .catch((err) => console.error('Error fetching work types:', err))
        .finally(() => setLoadingWorkTypes(false));
    }
  }, [isStorageLoaded, activeEmpId, isClientVisible, isWorkTypeVisible]);

  useEffect(() => {
    if (!isClientVisible || !selectedClient?.value || !activeEmpId) {
      if (isClientVisible && !selectedClient) setProjectOptions([]);
      return;
    }

    setLoadingProjects(true);
    fetchAPI(`api/Projects/GetAccountProjectsForTimesheet/${selectedClient.value}/${activeEmpId}`, 'GET', '', true)
      .then((res) => res.json())
      .then((data) => {
        const list = Array.isArray(data) ? data : data?.data || [];
        const formatted = list.map((item: any) => ({
          value: item.AccountProjectId,
          label: item.ProjectName,
        }));
        setProjectOptions(formatted);

        const targetId = savedIds.projectId;
        if (targetId) {
          const match = formatted.find((p: any) => Number(p.value) === Number(targetId));
          if (match) setSelectedProject(match);
        }
      })
      .catch((err) => console.error('Error fetching projects:', err))
      .finally(() => setLoadingProjects(false));
  }, [selectedClient, activeEmpId, isClientVisible]);

  useEffect(() => {
    if (!selectedProject?.value || !activeEmpId) {
      setTaskOptions([]);
      return;
    }

    setLoadingTasks(true);
    fetchAPI(`api/Timesheets/GetAccountProjectTasksForTimesheet/${selectedProject.value}/${activeEmpId}`, 'GET', '', true)
      .then((res) => res.json())
      .then((data) => {
        const list = Array.isArray(data) ? data : data?.data || [];
        const formatted = list.map((item: any) => ({
          value: item.AccountProjectTaskId,
          label: item.TaskName,
        }));
        setTaskOptions(formatted);

        const targetId = savedIds.taskId;
        if (targetId) {
          const match = formatted.find((t: any) => Number(t.value) === Number(targetId));
          if (match) setSelectedTask(match);
        }
      })
      .catch((err) => console.error('Error fetching tasks:', err))
      .finally(() => setLoadingTasks(false));
  }, [selectedProject, activeEmpId]);

  useEffect(() => {
    if (!isCostCenterVisible || !selectedTask?.value || !selectedProject?.value || !activeEmpId) {
      setCostCenterOptions([]);
      return;
    }

    setLoadingCostCenters(true);
    fetchAPI(`api/v2/CostCenter/GetAccountCostCenterForTimesheet/${activeEmpId}/${selectedProject.value}/${selectedTask.value}`, 'GET', '', true)
      .then((res) => res.json())
      .then((data) => {
        const list = Array.isArray(data) ? data : data?.data || [];
        const formatted = list.map((item: any) => ({
          value: item.AccountCostCenterId,
          label: item.AccountCostCenter,
        }));
        setCostCenterOptions(formatted);

        const targetId = savedIds.costCenterId;
        if (targetId) {
          const match = formatted.find((c: any) => Number(c.value) === Number(targetId));
          if (match) setSelectedCostCenter(match);
        }
      })
      .catch((err) => console.error('Error fetching cost centers:', err))
      .finally(() => setLoadingCostCenters(false));
  }, [selectedTask, selectedProject, activeEmpId, isCostCenterVisible]);

  const convertTimeToDecimal = (timeStr: string): number => {
    if (!timeStr) return 0;

    if (decimalFormat) {
      const separator = timeStr.includes('.') ? '.' : ':';
      const [h, m] = timeStr.split(separator).map(Number);
      return Number(((h || 0) + (m || 0) / 100).toFixed(2));
    }

    if (timeStr.includes(':')) {
      const [h, m] = timeStr.split(':').map(Number);
      return Number(((h || 0) + (m || 0) / 60).toFixed(2));
    }

    const val = parseFloat(timeStr);
    return isNaN(val) ? 0 : val;
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>, setHours: (val: string) => void) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length > 4) value = value.slice(0, 4);

    const separator = decimalFormat ? '.' : ':';
    const maxMinutes = decimalFormat ? 99 : 59;

    let formattedValue = value;
    if (value.length >= 3) {
      formattedValue = `${value.slice(0, 2)}${separator}${value.slice(2)}`;
    }

    setHours(formattedValue);

    if (formattedValue.length === 5) {
      const [h, m] = formattedValue.split(separator).map(Number);

      if (h >= 24) {
        setTimeError('Invalid Time! Hours must be less than 24');
      } else if (m > maxMinutes) {
        setTimeError(`Invalid Time! Minutes must not exceed ${maxMinutes}`);
      } else {
        setTimeError('');
      }
    } else if (formattedValue.length <= 2 && formattedValue !== '') {
      const h = Number(formattedValue);
      if (h >= 24) {
        setTimeError('Invalid Time! Hours must be less than 24');
      } else {
        setTimeError('');
      }
    } else {
      setTimeError('');
    }
  };

  const handleTimeBlur = () => {
    if (!hours) return;

    const separator = decimalFormat ? '.' : ':';
    const maxMinutes = decimalFormat ? 99 : 59;

    let formatted = hours;

    if (!hours.includes(separator)) {
      if (hours.length === 1) formatted = `0${hours}${separator}00`;
      else if (hours.length === 2) formatted = `${hours}${separator}00`;
      setHours(formatted);
    }

    if (formatted.includes(separator)) {
      const [h, m] = formatted.split(separator).map(Number);
      if (h >= 24) {
        setTimeError('Invalid Time! Hours must be less than 24');
      } else if (m > maxMinutes) {
        setTimeError(`Invalid Time! Minutes must not exceed ${maxMinutes}`);
      } else {
        setTimeError('');
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const allowedKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete'];
    if (allowedKeys.includes(e.key)) return;
    if (!/^\d$/.test(e.key)) e.preventDefault();
  };

  const handleSaveTimeEntry = async () => {
    setTimeError('');
    setDescriptionError('');
    setCustomFieldError('');

    if (!selectedTask || !selectedTask.value) {
      alert('Please select a Task.');
      return;
    }

    const hoursDecimal = convertTimeToDecimal(hours);
    if (!hours || hours.trim() === '' || hours === '00:00' || hours === '00.00' || hours === '__:__' || hoursDecimal <= 0) {
      setTimeError('Please enter valid hours (minimum 1 minute).');
      return;
    }

    if (timeError) {
      return;
    }

    if (isDescriptionRequired && !description.trim()) {
      setDescriptionError('Description is required.');
      return;
    }

    if (customFieldsTemplate && customFieldsTemplate.length > 0) {
      for (const field of customFieldsTemplate) {
        if (!field.IsDisabled && field.IsRequired) {
          const val = cfValues[field.DatabaseFieldName];
          if (!val || val === "None" || (typeof val === 'string' && val.trim() === '')) {
            setCustomFieldError(`${field.CustomFieldCaption} is required.`);
            ShowToast('Error', `${field.CustomFieldCaption} is required.`, 'error', 'toast-top-right');
            return;
          }
        }
      }
    }

    setIsSaving(true);
    try {
      let periodId = 0;
      let periodStartDate = entryDate;
      let periodEndDate = entryDate;
      let timesheetPeriodType = 'Weekly';

      try {
        const periodRes = await fetchAPI(`api/Timesheets/GetTimesheetPeriodForReact/${activeEmpId}/${entryDate}`, 'GET', '', true);
        if (periodRes.status === 200) {
          const periodJson = await periodRes.json();
          const targetData = periodJson?.data || periodJson;
          const periodObj = targetData?.AccountEmployeeTimeEntryPeriod || {};

          periodId = periodObj?.AccountEmployeeTimeEntryPeriodId || null;
          periodStartDate = periodObj?.TimeEntryStartDate || entryDate;
          periodEndDate = periodObj?.TimeEntryEndDate || entryDate;
          timesheetPeriodType = periodObj?.TimeEntryViewType || 'Weekly';
        }
      } catch (periodErr) {
        console.warn('Could not fetch period info, proceeding with defaults:', periodErr);
      }

      const postObject = {
        AccountEmployeeTimeEntryPeriodId: periodId,
        AccountEmployeeTimeEntryApprovalProjectId: null,
        AccountEmployeeTimeEntryId: 0,
        AccountEmployeeId: Number(activeEmpId),
        AccountClientId: isClientVisible ? selectedClient?.value || 0 : 0,
        AccountProjectId: selectedProject?.value || 0,
        AccountProjectTaskId: selectedTask?.value || 0,
        AccountCostCenterId: isCostCenterVisible ? selectedCostCenter?.value || 0 : 0,
        AccountWorkTypeId: isWorkTypeVisible ? selectedWorkType?.value || defaultWorkTypeId || 0 : defaultWorkTypeId || 0,
        AccountTimeOffTypeId: null,
        Hours: hoursDecimal,
        Percentage: null,
        PeriodStartDate: periodStartDate,
        PeriodEndDate: periodEndDate,
        TimeEntryDate: entryDate,
        StartTime: isClockPopoverActive && startTime ? `${entryDate}T${startTime}:00` : null,
        EndTime: isClockPopoverActive && endTime ? `${entryDate}T${endTime}:00` : null,
        IsTimeOff: false,
        TimesheetPeriodType: timesheetPeriodType,
        IsSubmitted: false,
        Description: description,
        Reason: '',
        TimeZoneId: timeZoneId,
        showClockStartEnd: isClockPopoverActive,
        SystemPlatformSourceId: '50E254BE-D515-4FBA-BFB7-DFD27852D023',
        ...cfValues
      };
    
      const response = await fetchAPI('api/Timesheets/AddAccountEmployeeTimeEntry', 'POST', postObject, true);
      if (response.ok) {
        ShowToast('Success', 'Time Entry Added Successfully!', 'success', 'toast-top-right');
        setHours('');
        setDescription('');
        onBackToTimer();
      } else {
        const errData = await response.json().catch(() => null);
        ShowToast('Error', errData?.message || 'Failed to save time entry.', 'error', 'toast-top-right');
      }
    } catch (error) {
      console.error('Error saving time entry:', error);
      alert('An error occurred while saving.');
    } finally {
      setIsSaving(false);
    }
  };

  const isHoursValid = Boolean(hours) && hours !== '00:00' && hours !== '00.00' && hours !== '__:__' && convertTimeToDecimal(hours) > 0;
return (
  <div className="d-flex flex-column h-100" style={{ height: '100%', maxHeight: '100vh', overflow: 'hidden' }}>

    <div className="flex-grow-1 pe-1" style={{ overflowY: 'auto', flex: '1 1 auto', minHeight: 0 }}>
      <button
        type="button"
        onClick={onBackToTimer}
        className="btn btn-link btn-color-primary p-0 fs-8 fw-bold mb-3 text-decoration-none d-flex align-items-center me-auto"
      >
        <i className="bi bi-arrow-left fs-7 me-1"></i> Back to Track Time
      </button>

      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="fw-bolder text-dark fs-6 m-0" style={{ color: '#181C32' }}>
          Enter Time Manually
        </h6>
        {(selectedClient || selectedProject || selectedTask || selectedCostCenter || selectedWorkType || hours || description) && (
          <button
            type="button"
            onClick={handleClearAll}
            className="btn btn-icon btn-active-light-danger btn-sm p-1 border-0"
            title="Clear all dropdowns and fields"
            style={{ width: '28px', height: '28px' }}
          >
            <i className="bi bi-arrow-counterclockwise fs-7 text-danger"></i>
          </button>
        )}
      </div>

      {isClientVisible && (
        <div className="mb-4">
          <label className="form-label fs-7 fw-bold mb-1 extensionHeading" style={{ color: '#3F4254' }}>Client</label>
          <Select
            value={selectedClient}
            onChange={handleClientChange}
            options={clientOptions}
            isLoading={loadingClients}
            placeholder="Select Client"
            styles={cleanDropdownStyles}
            formatOptionLabel={(opt, { context }) => formatOptionWithIcon(opt, 'bi-building', context)}
          />
        </div>
      )}

      <div className="mb-4">
        <label className="form-label fs-7 fw-bold mb-1 extensionHeading" style={{ color: '#3F4254' }}>Project</label>
        <Select
          value={selectedProject}
          onChange={handleProjectChange}
          options={projectOptions}
          isLoading={loadingProjects}
          isDisabled={isClientVisible && !selectedClient}
          placeholder="Select Project"
          styles={cleanDropdownStyles}
          formatOptionLabel={(opt, { context }) => formatOptionWithIcon(opt, 'bi-folder', context)}
        />
      </div>

      <div className="mb-4">
        <label className="form-label fs-7 fw-bold mb-1 extensionHeading"  style={{ color: '#3F4254' }}>Task</label>
        <Select
          value={selectedTask}
          onChange={handleTaskChange}
          options={taskOptions}
          isLoading={loadingTasks}
          isDisabled={!selectedProject}
          placeholder="Select Task"
          styles={cleanDropdownStyles}
          formatOptionLabel={(opt, { context }) => formatOptionWithIcon(opt, 'bi-card-checklist', context)}
        />
      </div>

      {isCostCenterVisible && (
        <div className="mb-4">
          <label className="form-label fs-7 fw-bold mb-1 extensionHeading">Cost Center</label>
          <Select
            value={selectedCostCenter}
            onChange={handleCostCenterChange}
            options={costCenterOptions}
            isLoading={loadingCostCenters}
            isDisabled={!selectedTask}
            placeholder="Select Cost Center"
            styles={cleanDropdownStyles}
            formatOptionLabel={(opt, { context }) => formatOptionWithIcon(opt, 'bi-diagram-3', context)}
          />
        </div>
      )}

      {isWorkTypeVisible && (
        <div className="mb-4">
          <label className="form-label fs-7 fw-bold mb-1 extensionHeading" style={{ color: '#3F4254' }}>Work Type</label>
          <Select
            value={selectedWorkType}
            onChange={handleWorkTypeChange}
            options={workTypeOptions}
            isLoading={loadingWorkTypes}
            placeholder="Select Work Type"
            styles={cleanDropdownStyles}
            formatOptionLabel={(opt, { context }) => formatOptionWithIcon(opt, 'bi-briefcase', context)}
          />
        </div>
      )}

      {/* <div className="mb-3">
        <label className="form-label fs-7 fw-bold text-dark mb-1">Date</label>
        <input
          type="date"
          className="form-control rounded-3 fw-semibold text-dark"
          style={{ height: '38px', fontSize: '13px', borderColor: '#E4E6EF' }}
          value={entryDate}
          onChange={(e) => setEntryDate(e.target.value)}
        />
      </div> */}
      <div className="mb-4">
        <style>{`
          /* Native right-side calendar icon ko hide karne ke liye */
          input[type="date"]::-webkit-calendar-picker-indicator {
            background: transparent;
            cursor: pointer;
            position: absolute;
            top: 0; right: 0; bottom: 0; left: 0;
            width: 100%;
            height: 100%;
            opacity: 0;
          }
          .date-input-wrapper {
            position: relative;
          }
        `}</style>
        <label className="form-label fs-7 fw-bold mb-1 extensionHeading" style={{ color: '#3F4254' }}>Date</label>
        <div className="input-group date-input-wrapper">
          <span 
            className="input-group-text border border-end-0 d-flex align-items-center justify-content-center px-3 rounded-start-3"
            style={{ height: '38px', borderColor: '#E4E6EF', backgroundColor: '#F9FAFB' }}
          >
            <i className="bi bi-calendar fs-7 text-gray-600"></i>
          </span>
          <div className="position-relative flex-grow-1">
            <input
              type="date"
              className="form-control rounded-start-0 rounded-end-3 fw-semibold text-dark"
              style={{ height: '38px', fontSize: '13px', borderColor: '#E4E6EF', backgroundColor: '#FFFFFF' }}
              value={entryDate}
              onChange={(e) => setEntryDate(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* {isClockPopoverActive ? (
        <div className="mb-3 position-relative">
          <label className="form-label fs-7 fw-bold text-dark mb-1">
            Hours <span className="text-danger">*</span>
          </label>
          <div className="input-group">
            <input
              type="text"
              readOnly
              className={`form-control bg-light cursor-pointer rounded-start-3 fw-semibold ${timeError ? 'is-invalid' : ''}`}
              style={{ height: '38px', fontSize: '13px', borderColor: '#E4E6EF' }}
              value={hours ? hours : ''}
              placeholder="Click to select start & end time"
              onClick={() => setShowTimePopover(true)}
            />
            <button
              className="btn btn-secondary border border-start-0 d-flex align-items-center justify-content-center px-3 rounded-end-3"
              type="button"
              style={{ height: '38px', borderColor: '#E4E6EF' }}
              onClick={() => setShowTimePopover(!showTimePopover)}
            >
              <i className="bi bi-clock fs-7"></i>
            </button>
          </div>

          {timeError && (
            <div className="text-danger fs-9 mt-1 fw-semibold">
              {timeError}
            </div>
          )}

          {showTimePopover && (
            <ExtensionTimeEntryPopover
              initialStartTime={startTime}
              initialEndTime={endTime}
              onApply={(start, end, calculatedHours) => {
                setStartTime(start);
                setEndTime(end);
                setHours(calculatedHours);
                setTimeError('');
              }}
              onClose={() => setShowTimePopover(false)}
            />
          )}
        </div>
      ) 
      : (
        <div className="mb-3">
          <label className="form-label fs-7 fw-bold text-dark mb-1">
            Hours <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            className={`form-control rounded-3 fw-semibold ${timeError ? 'is-invalid' : ''}`}
            style={{ height: '38px', fontSize: '13px', borderColor: '#E4E6EF' }}
            placeholder={decimalFormat ? '00.00' : '00:00'}
            maxLength={5}
            value={hours}
            onChange={(e) => handleTimeChange(e, setHours)}
            onBlur={handleTimeBlur}
            onKeyDown={handleKeyDown}
          />
          {timeError && (
            <div className="text-danger fs-9 mt-1 fw-semibold">
              {timeError}
            </div>
          )}
        </div>
      )} */}
      {isClockPopoverActive ? (
  <div className="mb-4 position-relative">
    <label className="form-label fs-7 fw-bold mb-1 extensionHeading" style={{ color: '#3F4254' }}>      Hours <span className="text-danger">*</span>
    </label>
    <div className="input-group">
      
      <button
        className="btn btn-secondary border border-end-0 d-flex align-items-center justify-content-center px-3 rounded-start-3"
        type="button"
        style={{ height: '38px', borderColor: '#E4E6EF', backgroundColor: '#F9FAFB' }}
        onClick={() => setShowTimePopover(!showTimePopover)}
      >
        <i className="bi bi-clock fs-7"></i>
      </button>
      <input
        type="text"
        readOnly
        className={`form-control bg-light cursor-pointer rounded-end-3 fw-semibold ${timeError ? 'is-invalid' : ''}`}
        style={{ height: '38px', fontSize: '13px', borderColor: '#E4E6EF' }}
        value={hours ? hours : ''}
        placeholder="Click to select start & end time"
        onClick={() => setShowTimePopover(true)}
      />
    </div>

    {timeError && (
      <div className="text-danger fs-9 mt-1 fw-semibold">
        {timeError}
      </div>
    )}

    {showTimePopover && (
      <ExtensionTimeEntryPopover
        initialStartTime={startTime}
        initialEndTime={endTime}
        onApply={(start, end, calculatedHours) => {
          setStartTime(start);
          setEndTime(end);
          setHours(calculatedHours);
          setTimeError('');
        }}
        onClose={() => setShowTimePopover(false)}
      />
    )}
  </div>
) : (
  
  <div className="mb-4">
    <label className="form-label fs-7 fw-bold mb-1 extensionHeading" style={{ color: '#3F4254' }}>
      Hours <span className="text-danger">*</span>
    </label>
    <input
      type="text"
      className={`form-control rounded-3 fw-semibold ${timeError ? 'is-invalid' : ''}`}
      style={{ height: '38px', fontSize: '13px', borderColor: '#E4E6EF' }}
      placeholder={decimalFormat ? '00.00' : '00:00'}
      maxLength={5}
      value={hours}
      onChange={(e) => handleTimeChange(e, setHours)}
      onBlur={handleTimeBlur}
      onKeyDown={handleKeyDown}
    />
    {timeError && (
      <div className="text-danger fs-9 mt-1 fw-semibold">
        {timeError}
      </div>
    )}
  </div>
)}

      <div className="mb-4">
        <label className="form-label fs-7 fw-bold fontwe mb-1 extensionHeading" style={{ color: '#3F4254' }}>
          Description {isDescriptionRequired && <span className="text-danger"> *</span>}
        </label>
        <textarea
          value={description}
          onChange={(e) => {
            setDescription(e.target.value.slice(0, 500));
            if (descriptionError) setDescriptionError('');
          }}
          className={`form-control rounded-3 ${descriptionError ? 'is-invalid' : ''}`}
          style={{ fontSize: '13px', resize: 'none', borderColor: '#E4E6EF' }}
          rows={3}
          placeholder="What did you work on?"
        />
        {descriptionError && (
          <div className="text-danger fs-9 mt-1 fw-semibold">
            {descriptionError}
          </div>
        )}
      </div>

      {customFieldsTemplate && customFieldsTemplate.filter((x: any) => !x.IsDisabled).map((x: any, index: any) => {
        const isFieldInvalid = customFieldError && x.IsRequired && (!cfValues[x.DatabaseFieldName] || cfValues[x.DatabaseFieldName] === "None" || String(cfValues[x.DatabaseFieldName]).trim() === "");
        
        return (
          <div key={index} className={"mb-4 custom-field-item"}>
            <label className={"form-label fs-7 fw-bold mb-1 extensionHeading"} style={{ color: '#3F4254' }}>
              {x.CustomFieldCaption} {x.IsRequired && <span className="text-danger">*</span>}
            </label>
            
            {x.MasterCustomDataTypeName == "Drop-Down" && (
              <select
                name={x.DatabaseFieldName}
                className={`form-control form-control-sm input-sm custom-field cf-select rounded-3 ${isFieldInvalid ? 'is-invalid' : ''}`}
                style={{ height: '38px', fontSize: '13px', borderColor: '#E4E6EF' }}
                onChange={handleCustomFieldChange}
                value={(cfValues[x.DatabaseFieldName] ? cfValues[x.DatabaseFieldName] : x.DefaultValue)}
              >
                <option value="None">None</option>
                {x.DropDownOptions != null &&
                  x.DropDownOptions.split("+").map((value: any, idx: any) =>
                    <option key={idx} value={value}>{value}</option>
                  )
                }
              </select>
            )}

            {x.MasterCustomDataTypeName == "Date" && (
              <DatePicker
                name={x.DatabaseFieldName}
                onChange={(date) => handleCustomFieldDatePicker(date, x.DatabaseFieldName)}
                calendarStartDay={calendarStartDay}
                selected={(cfValues[x.DatabaseFieldName] ? dateFormatByAccountCulture(cfValues[x.DatabaseFieldName], cultureInfoName?.toString() || '') : new Date())}
                className={`form-control form-control-sm input-sm rounded-3 fw-semibold text-dark ${isFieldInvalid ? 'is-invalid' : ''}`}
                dateFormat={CurrentCultureDateFormat?.toString() || 'yyyy-MM-dd'}
              />
            )}

            {x.MasterCustomDataTypeName == "Text" && (
              <input
                name={x.DatabaseFieldName}
                value={cfValues[x.DatabaseFieldName] || ''}
                onChange={handleCustomFieldChange}
                className={`form-control form-control-sm input-sm custom-field rounded-3 ${isFieldInvalid ? 'is-invalid' : ''}`}
                style={{ height: '38px', fontSize: '13px', borderColor: '#E4E6EF' }}
                maxLength={x.MaximumLength ? x.MaximumLength : undefined}
                type="text"
              />
            )}

            {x.MasterCustomDataTypeName == "Number" && (
              <input
                name={x.DatabaseFieldName}
                value={cfValues[x.DatabaseFieldName] || ''}
                onChange={(e) => {
                  const val = e.target.value;
                  if (validateCultureNumber(val, cultureInfoName?.toString() || '', decimalPlaces)) {
                    setCfValues({ ...cfValues, [x.DatabaseFieldName]: val });
                    if (customFieldError) setCustomFieldError('');
                  }
                }}
                onBlur={(e) => {
                  const formatted = formatNumberCustomoriginal(
                    cultureInfoName?.toString() || '',
                    e.target.value,
                    decimalPlaces
                  );
                  setCfValues({ ...cfValues, [x.DatabaseFieldName]: formatted });
                }}
                className={`form-control form-control-sm input-sm custom-field rounded-3 ${isFieldInvalid ? 'is-invalid' : ''}`}
                style={{ height: '38px', fontSize: '13px', borderColor: '#E4E6EF' }}
                maxLength={x.MaximumLength ? x.MaximumLength : undefined}
                type="text"
              />
            )}

            {isFieldInvalid && (
              <div className="text-danger fs-9 mt-1 fw-semibold">
                {x.CustomFieldCaption} is required.
              </div>
            )}
          </div>
        );
      })}
    </div>

 
    <div className="pt-2 bg-white border-top mt-auto" style={{ flexShrink: 0 }}>
      <button
        type="button"
        disabled={
          !selectedTask ||
          isSaving ||
          Boolean(timeError) ||
          !isHoursValid ||
          Boolean(descriptionError)
        }
        onClick={handleSaveTimeEntry}
        className="btn btn-primary btn-sm w-100 fs-6 fw-bold py-2"
        style={{ backgroundColor: '#1B64F2', borderColor: '#1B64F2', borderRadius: '10px' }}
      >
        {isSaving ? 'Saving...' : 'Save Time Entry'}
      </button>
    </div>
  </div>
);
};

export default ExtensionManualEntry;



// import React, { useState, useEffect } from 'react';
// import { useAppSelector } from '../../app/hooks';
// import Select from 'react-select';
// import fetchAPI from '../../common/fetchAPI';
// import ExtensionTimeEntryPopover from './ExtensionTimeEntryPopover';
// import { ShowToast } from '../../common/Toastr';

// declare const chrome: any;

// interface ExtensionManualEntryProps {
//   onBackToTimer: () => void;
// }

// const formatOptionWithIcon = (option: any, iconClass: string, context: 'menu' | 'value') => {
//   if (context === 'value') {
//     return (
//       <div className="d-flex align-items-center">
//         <i className={`bi ${iconClass} me-2 text-gray-700 fs-6`}></i>
//         <span className="fw-bold fs-7" style={{ color: '#181C32' }}>
//           {option.label}
//         </span>
//       </div>
//     );
//   }


//   return option.label;
// };

// const cleanDropdownStyles = {
//   control: (base: any, state: any) => ({
//     ...base,
//     minHeight: '38px',
//     height: '38px',
//     borderRadius: '8px',
//     borderColor: state.isFocused ? '#1B64F2' : '#dddddd',
//     boxShadow: 'none',
//     backgroundColor: state.isDisabled ? '#F9FAFB' : '#FFFFFF',
//     '&:hover': {
//       borderColor: '#1B64F2',
//     },
//   }),
//   valueContainer: (base: any) => ({
//     ...base,
//     padding: '0 8px',
//     height: '38px',
//     display: 'flex',
//     alignItems: 'center',
//   }),
//   input: (base: any) => ({
//     ...base,
//     margin: 0,
//     padding: 0,
//     fontSize: '13px',
//   }),
//   singleValue: (base: any) => ({
//     ...base,
//     fontSize: '13px',
//     color: '#181C32', 
//     margin: 0,
//     display: 'flex',
//     alignItems: 'center',
//   }),
//   placeholder: (base: any) => ({
//     ...base,
//     fontSize: '13px',
//     color: '#A1A5B7',
//   }),
//   option: (base: any, state: any) => ({
//     ...base,
//     backgroundColor: state.isSelected 
//       ? '#1B64F2' 
//       : state.isFocused 
//         ? '#F4F6F9' 
//         : '#FFFFFF',
//     color: state.isSelected ? '#FFFFFF' : '#181C32',
//     fontSize: '13px',
//     cursor: 'pointer',
//     padding: '8px 12px',
//     fontWeight: state.isSelected ? '500' : '400',
//   }),
//   menu: (base: any) => ({
//     ...base,
//     borderRadius: '8px',
//     boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.08)',
//     zIndex: 9999,
//     overflow: 'hidden',
//   }),
//   menuList: (base: any) => ({
//     ...base,
//     padding: 0,
//   }),
// };
// export const ExtensionManualEntry: React.FC<ExtensionManualEntryProps> = ({ onBackToTimer }) => {
//   const {
//     sessionAccountEmployeeId,
//     timeZoneId,
//     showClientInTimesheet,
//     showCostCenterInTimeSheet,
//     showWorkTypeInTimeSheet,
//     IsTimeEntryDescriptionRequired,
//     showClockStartEnd,
//     decimalFormat, 
//   } = useAppSelector((state) => state.globalState);

//   const { defaultWorkTypeId } = useAppSelector((state) => state.timesheet);

//   const getCurrentFormattedTime = (offsetHours = 0): string => {
//     const now = new Date();
//     now.setHours(now.getHours() + offsetHours);
//     const hours = String(now.getHours()).padStart(2, '0');
//     const minutes = String(now.getMinutes()).padStart(2, '0');
//     return `${hours}:${minutes}`;
//   };

//   const showClientFlag: boolean = Boolean(showClientInTimesheet);
//   const showCostCenterFlag: boolean = Boolean(showCostCenterInTimeSheet);
//   const showWorkTypeFlag: boolean = Boolean(showWorkTypeInTimeSheet);

//   const isClockPopoverActive: boolean = Boolean(showClockStartEnd) && !Boolean(decimalFormat);

//   const [activeEmpId, setActiveEmpId] = useState<number | string | null>(null);
//   const [isClientVisible, setIsClientVisible] = useState<boolean>(showClientFlag);
//   const [isCostCenterVisible, setIsCostCenterVisible] = useState<boolean>(showCostCenterFlag);
//   const [isWorkTypeVisible, setIsWorkTypeVisible] = useState<boolean>(showWorkTypeFlag);

//   const [isStorageLoaded, setIsStorageLoaded] = useState<boolean>(false);

//   const [clientOptions, setClientOptions] = useState<{ value: any; label: string }[]>([]);
//   const [projectOptions, setProjectOptions] = useState<{ value: any; label: string }[]>([]);
//   const [taskOptions, setTaskOptions] = useState<{ value: any; label: string }[]>([]);
//   const [costCenterOptions, setCostCenterOptions] = useState<{ value: any; label: string }[]>([]);
//   const [workTypeOptions, setWorkTypeOptions] = useState<{ value: any; label: string }[]>([]);

//   const [selectedClient, setSelectedClient] = useState<any>(null);
//   const [selectedProject, setSelectedProject] = useState<any>(null);
//   const [selectedTask, setSelectedTask] = useState<any>(null);
//   const [selectedCostCenter, setSelectedCostCenter] = useState<any>(null);
//   const [selectedWorkType, setSelectedWorkType] = useState<any>(null);

//   const [timeError, setTimeError] = useState<string>('');
//   const [descriptionError, setDescriptionError] = useState<string>('');

//   const [savedIds, setSavedIds] = useState<{
//     clientId?: any;
//     projectId?: any;
//     taskId?: any;
//     costCenterId?: any;
//     workTypeId?: any;
//   }>({});

//   const [loadingClients, setLoadingClients] = useState(false);
//   const [loadingProjects, setLoadingProjects] = useState(false);
//   const [loadingTasks, setLoadingTasks] = useState(false);
//   const [loadingCostCenters, setLoadingCostCenters] = useState(false);
//   const [loadingWorkTypes, setLoadingWorkTypes] = useState(false);
//   const [isSaving, setIsSaving] = useState(false);

//   const [entryDate, setEntryDate] = useState<string>(new Date().toISOString().split('T')[0]);
  
//   const [hours, setHours] = useState<string>('');
//   const [startTime, setStartTime] = useState<string>(() => getCurrentFormattedTime(0));
//   const [endTime, setEndTime] = useState<string>(() => getCurrentFormattedTime(0));
//   const [showTimePopover, setShowTimePopover] = useState<boolean>(false);
//   const [description, setDescription] = useState<string>('');
//   const [isDescriptionRequired, setIsDescriptionRequired] = useState<boolean>(Boolean(IsTimeEntryDescriptionRequired));

//   const persistStorage = (dataToSave: Record<string, any>) => {
//     if (typeof chrome !== 'undefined' && chrome.storage) {
//       chrome.storage.local.set(dataToSave);
//     } else {
//       Object.entries(dataToSave).forEach(([k, v]) => {
//         if (v === null || v === undefined) localStorage.removeItem(k);
//         else localStorage.setItem(k, JSON.stringify(v));
//       });
//     }
//   };

//   useEffect(() => {
//     const keysToFetch = [
//       'sessionAccountEmployeeId',
//       'accountEmployeeId',
//       'showClientInTimesheet',
//       'showCostCenterInTimeSheet',
//       'showWorkTypeInTimeSheet',
//       'manual_clientId',
//       'manual_projectId',
//       'manual_taskId',
//       'manual_costCenterId',
//       'manual_workTypeId',
//       'IsTimeEntryDescriptionRequired',
//     ];

//     if (typeof chrome !== 'undefined' && chrome.storage) {
//       chrome.storage.local.get(keysToFetch, (result: any) => {
//         const resolvedEmpId = result?.accountEmployeeId || result?.sessionAccountEmployeeId || sessionAccountEmployeeId;
//         if (resolvedEmpId) setActiveEmpId(Number(resolvedEmpId));

//         if (result?.showClientInTimesheet !== undefined) setIsClientVisible(Boolean(result.showClientInTimesheet));
//         if (result?.showCostCenterInTimeSheet !== undefined) setIsCostCenterVisible(Boolean(result.showCostCenterInTimeSheet));
//         if (result?.showWorkTypeInTimeSheet !== undefined) setIsWorkTypeVisible(Boolean(result.showWorkTypeInTimeSheet));
//         if (result?.IsTimeEntryDescriptionRequired !== undefined) {
//           setIsDescriptionRequired(Boolean(result.IsTimeEntryDescriptionRequired));
//         } else if (IsTimeEntryDescriptionRequired !== undefined && IsTimeEntryDescriptionRequired !== null) {
//           setIsDescriptionRequired(Boolean(IsTimeEntryDescriptionRequired));
//           persistStorage({ IsTimeEntryDescriptionRequired: Boolean(IsTimeEntryDescriptionRequired) });
//         }

//         setSavedIds({
//           clientId: result?.manual_clientId ?? null,
//           projectId: result?.manual_projectId ?? null,
//           taskId: result?.manual_taskId ?? null,
//           costCenterId: result?.manual_costCenterId ?? null,
//           workTypeId: result?.manual_workTypeId ?? null,
//         });

//         setIsStorageLoaded(true);
//       });
//     } else {
//       if (sessionAccountEmployeeId) setActiveEmpId(Number(sessionAccountEmployeeId));
//       setIsDescriptionRequired(Boolean(IsTimeEntryDescriptionRequired));
//       const parseLocal = (k: string) => {
//         const val = localStorage.getItem(k);
//         return val ? JSON.parse(val) : null;
//       };
//       setSavedIds({
//         clientId: parseLocal('manual_clientId'),
//         projectId: parseLocal('manual_projectId'),
//         taskId: parseLocal('manual_taskId'),
//         costCenterId: parseLocal('manual_costCenterId'),
//         workTypeId: parseLocal('manual_workTypeId'),
//       });
//       setIsStorageLoaded(true);
//     }
//   }, [sessionAccountEmployeeId, IsTimeEntryDescriptionRequired]);

//   const handleClearAll = () => {
//     setSelectedClient(null);
//     setSelectedProject(null);
//     setSelectedTask(null);
//     setSelectedCostCenter(null);
//     setSelectedWorkType(null);
//     setHours('');
//     setTimeError('');
//     setDescription('');
//     setSavedIds({});
//     setDescriptionError('');

//     persistStorage({
//       manual_clientId: null,
//       manual_projectId: null,
//       manual_taskId: null,
//       manual_costCenterId: null,
//       manual_workTypeId: null,
//     });
//   };

//   const handleClientChange = (opt: any) => {
//     setSelectedClient(opt);
//     const val = opt?.value ?? null;
//     setSavedIds((prev) => ({ ...prev, clientId: val, projectId: null, taskId: null, costCenterId: null }));
//     persistStorage({
//       manual_clientId: val,
//       manual_projectId: null,
//       manual_taskId: null,
//       manual_costCenterId: null,
//     });

//     if (!opt) {
//       setSelectedProject(null);
//       setSelectedTask(null);
//       setSelectedCostCenter(null);
//     }
//   };

//   const handleProjectChange = (opt: any) => {
//     setSelectedProject(opt);
//     const val = opt?.value ?? null;
//     setSavedIds((prev) => ({ ...prev, projectId: val, taskId: null, costCenterId: null }));
//     persistStorage({
//       manual_projectId: val,
//       manual_taskId: null,
//       manual_costCenterId: null,
//     });

//     if (!opt) {
//       setSelectedTask(null);
//       setSelectedCostCenter(null);
//     }
//   };

//   const handleTaskChange = (opt: any) => {
//     setSelectedTask(opt);
//     const val = opt?.value ?? null;
//     setSavedIds((prev) => ({ ...prev, taskId: val, costCenterId: null }));
//     persistStorage({
//       manual_taskId: val,
//       manual_costCenterId: null,
//     });

//     if (!opt) {
//       setSelectedCostCenter(null);
//     }
//   };

//   const handleCostCenterChange = (opt: any) => {
//     setSelectedCostCenter(opt);
//     const val = opt?.value ?? null;
//     setSavedIds((prev) => ({ ...prev, costCenterId: val }));
//     persistStorage({ manual_costCenterId: val });
//   };

//   const handleWorkTypeChange = (opt: any) => {
//     setSelectedWorkType(opt);
//     const val = opt?.value ?? null;
//     setSavedIds((prev) => ({ ...prev, workTypeId: val }));
//     persistStorage({ manual_workTypeId: val });
//   };

//   useEffect(() => {
//     if (!isStorageLoaded || !activeEmpId) return;

//     if (isClientVisible) {
//       setLoadingClients(true);
//       fetchAPI(`api/Timesheets/GetAccountClientForTimesheet/${activeEmpId}/0`, 'GET', '', true)
//         .then((res) => res.json())
//         .then((data) => {
//           const list = Array.isArray(data) ? data : data?.data || [];
//           const formatted = list.map((item: any) => ({
//             value: item.AccountClientId,
//             label: item.ClientName,
//           }));
//           setClientOptions(formatted);

//           const targetId = savedIds.clientId;
//           if (targetId) {
//             const match = formatted.find((c: any) => Number(c.value) === Number(targetId));
//             if (match) setSelectedClient(match);
//           }
//         })
//         .catch((err) => console.error('Error fetching clients:', err))
//         .finally(() => setLoadingClients(false));
//     } else {
//       setLoadingProjects(true);
//       fetchAPI(`api/Projects/GetAccountProjectsForTimesheet/0/${activeEmpId}`, 'GET', '', true)
//         .then((res) => res.json())
//         .then((data) => {
//           const list = Array.isArray(data) ? data : data?.data || [];
//           const formatted = list.map((item: any) => ({
//             value: item.AccountProjectId,
//             label: item.ProjectName,
//           }));
//           setProjectOptions(formatted);

//           const targetId = savedIds.projectId;
//           if (targetId) {
//             const match = formatted.find((p: any) => Number(p.value) === Number(targetId));
//             if (match) setSelectedProject(match);
//           }
//         })
//         .catch((err) => console.error('Error fetching projects:', err))
//         .finally(() => setLoadingProjects(false));
//     }

//     if (isWorkTypeVisible) {
//       setLoadingWorkTypes(true);
//       fetchAPI('api/WorkTypes', 'GET', '', true)
//         .then((res) => res.json())
//         .then((data) => {
//           const list = Array.isArray(data) ? data : data?.data || [];
//           const formatted = list.map((item: any) => ({
//             value: item.AccountWorkTypeId,
//             label: item.AccountWorkType,
//           }));
//           setWorkTypeOptions(formatted);

//           const targetId = savedIds.workTypeId || defaultWorkTypeId;
//           if (targetId) {
//             const match = formatted.find((w: any) => Number(w.value) === Number(targetId));
//             if (match) setSelectedWorkType(match);
//           } else if (formatted.length > 0) {
//             setSelectedWorkType(formatted[0]);
//           }
//         })
//         .catch((err) => console.error('Error fetching work types:', err))
//         .finally(() => setLoadingWorkTypes(false));
//     }
//   }, [isStorageLoaded, activeEmpId, isClientVisible, isWorkTypeVisible]);

//   useEffect(() => {
//     if (!isClientVisible || !selectedClient?.value || !activeEmpId) {
//       if (isClientVisible && !selectedClient) setProjectOptions([]);
//       return;
//     }

//     setLoadingProjects(true);
//     fetchAPI(`api/Projects/GetAccountProjectsForTimesheet/${selectedClient.value}/${activeEmpId}`, 'GET', '', true)
//       .then((res) => res.json())
//       .then((data) => {
//         const list = Array.isArray(data) ? data : data?.data || [];
//         const formatted = list.map((item: any) => ({
//           value: item.AccountProjectId,
//           label: item.ProjectName,
//         }));
//         setProjectOptions(formatted);

//         const targetId = savedIds.projectId;
//         if (targetId) {
//           const match = formatted.find((p: any) => Number(p.value) === Number(targetId));
//           if (match) setSelectedProject(match);
//         }
//       })
//       .catch((err) => console.error('Error fetching projects:', err))
//       .finally(() => setLoadingProjects(false));
//   }, [selectedClient, activeEmpId, isClientVisible]);

//   useEffect(() => {
//     if (!selectedProject?.value || !activeEmpId) {
//       setTaskOptions([]);
//       return;
//     }

//     setLoadingTasks(true);
//     fetchAPI(`api/Timesheets/GetAccountProjectTasksForTimesheet/${selectedProject.value}/${activeEmpId}`, 'GET', '', true)
//       .then((res) => res.json())
//       .then((data) => {
//         const list = Array.isArray(data) ? data : data?.data || [];
//         const formatted = list.map((item: any) => ({
//           value: item.AccountProjectTaskId,
//           label: item.TaskName,
//         }));
//         setTaskOptions(formatted);

//         const targetId = savedIds.taskId;
//         if (targetId) {
//           const match = formatted.find((t: any) => Number(t.value) === Number(targetId));
//           if (match) setSelectedTask(match);
//         }
//       })
//       .catch((err) => console.error('Error fetching tasks:', err))
//       .finally(() => setLoadingTasks(false));
//   }, [selectedProject, activeEmpId]);

//   useEffect(() => {
//     if (!isCostCenterVisible || !selectedTask?.value || !selectedProject?.value || !activeEmpId) {
//       setCostCenterOptions([]);
//       return;
//     }

//     setLoadingCostCenters(true);
//     fetchAPI(`api/v2/CostCenter/GetAccountCostCenterForTimesheet/${activeEmpId}/${selectedProject.value}/${selectedTask.value}`, 'GET', '', true)
//       .then((res) => res.json())
//       .then((data) => {
//         const list = Array.isArray(data) ? data : data?.data || [];
//         const formatted = list.map((item: any) => ({
//           value: item.AccountCostCenterId,
//           label: item.AccountCostCenter,
//         }));
//         setCostCenterOptions(formatted);

//         const targetId = savedIds.costCenterId;
//         if (targetId) {
//           const match = formatted.find((c: any) => Number(c.value) === Number(targetId));
//           if (match) setSelectedCostCenter(match);
//         }
//       })
//       .catch((err) => console.error('Error fetching cost centers:', err))
//       .finally(() => setLoadingCostCenters(false));
//   }, [selectedTask, selectedProject, activeEmpId, isCostCenterVisible]);

//   const convertTimeToDecimal = (timeStr: string): number => {
//     if (!timeStr) return 0;

//     if (decimalFormat) {
//       const separator = timeStr.includes('.') ? '.' : ':';
//       const [h, m] = timeStr.split(separator).map(Number);
//       return Number(((h || 0) + (m || 0) / 100).toFixed(2));
//     }

//     if (timeStr.includes(':')) {
//       const [h, m] = timeStr.split(':').map(Number);
//       return Number(((h || 0) + (m || 0) / 60).toFixed(2));
//     }

//     const val = parseFloat(timeStr);
//     return isNaN(val) ? 0 : val;
//   };

//   const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>, setHours: (val: string) => void) => {
//     let value = e.target.value.replace(/\D/g, '');
//     if (value.length > 4) value = value.slice(0, 4);

//     const separator = decimalFormat ? '.' : ':';
//     const maxMinutes = decimalFormat ? 99 : 59;

//     let formattedValue = value;
//     if (value.length >= 3) {
//       formattedValue = `${value.slice(0, 2)}${separator}${value.slice(2)}`;
//     }

//     setHours(formattedValue);

//     if (formattedValue.length === 5) {
//       const [h, m] = formattedValue.split(separator).map(Number);

//       if (h >= 24) {
//         setTimeError('Invalid Time! Hours must be less than 24');
//       } else if (m > maxMinutes) {
//         setTimeError(`Invalid Time! Minutes must not exceed ${maxMinutes}`);
//       } else {
//         setTimeError('');
//       }
//     } else if (formattedValue.length <= 2 && formattedValue !== '') {
//       const h = Number(formattedValue);
//       if (h >= 24) {
//         setTimeError('Invalid Time! Hours must be less than 24');
//       } else {
//         setTimeError('');
//       }
//     } else {
//       setTimeError('');
//     }
//   };

//   const handleTimeBlur = () => {
//     if (!hours) return;

//     const separator = decimalFormat ? '.' : ':';
//     const maxMinutes = decimalFormat ? 99 : 59;

//     let formatted = hours;

//     if (!hours.includes(separator)) {
//       if (hours.length === 1) formatted = `0${hours}${separator}00`;
//       else if (hours.length === 2) formatted = `${hours}${separator}00`;
//       setHours(formatted);
//     }

//     if (formatted.includes(separator)) {
//       const [h, m] = formatted.split(separator).map(Number);
//       if (h >= 24) {
//         setTimeError('Invalid Time! Hours must be less than 24');
//       } else if (m > maxMinutes) {
//         setTimeError(`Invalid Time! Minutes must not exceed ${maxMinutes}`);
//       } else {
//         setTimeError('');
//       }
//     }
//   };

//   const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
//     const allowedKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete'];
//     if (allowedKeys.includes(e.key)) return;
//     if (!/^\d$/.test(e.key)) e.preventDefault();
//   };

//   const handleSaveTimeEntry = async () => {
//     setTimeError('');
//     setDescriptionError('');

//     if (!selectedTask || !selectedTask.value) {
//       alert('Please select a Task.');
//       return;
//     }

//     const hoursDecimal = convertTimeToDecimal(hours);
//     if (!hours || hours.trim() === '' || hours === '00:00' || hours === '00.00' || hours === '__:__' || hoursDecimal <= 0) {
//       setTimeError('Please enter valid hours (minimum 1 minute).');
//       return;
//     }

//     if (timeError) {
//       return;
//     }

//     if (isDescriptionRequired && !description.trim()) {
//       setDescriptionError('Description is required.');
//       return;
//     }

//     setIsSaving(true);
//     try {
//       let periodId = 0;
//       let periodStartDate = entryDate;
//       let periodEndDate = entryDate;
//       let timesheetPeriodType = 'Weekly';

//       try {
//         const periodRes = await fetchAPI(`api/Timesheets/GetTimesheetPeriodForReact/${activeEmpId}/${entryDate}`, 'GET', '', true);
//         if (periodRes.status === 200) {
//           const periodJson = await periodRes.json();
//           const targetData = periodJson?.data || periodJson;
//           const periodObj = targetData?.AccountEmployeeTimeEntryPeriod || {};

//           periodId = periodObj?.AccountEmployeeTimeEntryPeriodId || null;
//           periodStartDate = periodObj?.TimeEntryStartDate || entryDate;
//           periodEndDate = periodObj?.TimeEntryEndDate || entryDate;
//           timesheetPeriodType = periodObj?.TimeEntryViewType || 'Weekly';
//         }
//       } catch (periodErr) {
//         console.warn('Could not fetch period info, proceeding with defaults:', periodErr);
//       }

//       const postObject = {
//         AccountEmployeeTimeEntryPeriodId: periodId,
//         AccountEmployeeTimeEntryApprovalProjectId: null,
//         AccountEmployeeTimeEntryId: 0,
//         AccountEmployeeId: Number(activeEmpId),
//         AccountClientId: isClientVisible ? selectedClient?.value || 0 : 0,
//         AccountProjectId: selectedProject?.value || 0,
//         AccountProjectTaskId: selectedTask?.value || 0,
//         AccountCostCenterId: isCostCenterVisible ? selectedCostCenter?.value || 0 : 0,
//         AccountWorkTypeId: isWorkTypeVisible ? selectedWorkType?.value || defaultWorkTypeId || 0 : defaultWorkTypeId || 0,
//         AccountTimeOffTypeId: null,
//         Hours: hoursDecimal,
//         Percentage: null,
//         PeriodStartDate: periodStartDate,
//         PeriodEndDate: periodEndDate,
//         TimeEntryDate: entryDate,
//         StartTime: isClockPopoverActive && startTime ? `${entryDate}T${startTime}:00` : null,
//         EndTime: isClockPopoverActive && endTime ? `${entryDate}T${endTime}:00` : null,
//         IsTimeOff: false,
//         TimesheetPeriodType: timesheetPeriodType,
//         IsSubmitted: false,
//         Description: description,
//         Reason: '',
//         TimeZoneId: timeZoneId,
//         showClockStartEnd: isClockPopoverActive,
//         SystemPlatformSourceId: '50E254BE-D515-4FBA-BFB7-DFD27852D023',
//       };

//        const response = await fetchAPI('api/Timesheets/AddAccountEmployeeTimeEntry', 'POST', postObject, true);
//       if (response.ok) {
//         ShowToast('Success', 'Time Entry Added Successfully!', 'success', 'toast-top-right');
//         setHours('');
//         setDescription('');
//         onBackToTimer();
//       } else {
//         const errData = await response.json().catch(() => null);
//         ShowToast('Error', errData?.message || 'Failed to save time entry.', 'error', 'toast-top-right');
//       }
//     } catch (error) {
//       console.error('Error saving time entry:', error);
//       alert('An error occurred while saving.');
//     } finally {
//       setIsSaving(false);
//     }
//   };

//   const isHoursValid = Boolean(hours) && hours !== '00:00' && hours !== '00.00' && hours !== '__:__' && convertTimeToDecimal(hours) > 0;

//   return (
//      <div className="d-flex flex-column h-100 justify-content-between">
//       <div>
 
//         <button
//           type="button"
//           onClick={onBackToTimer}
//           className="btn btn-link btn-color-primary p-0 fs-8 fw-bold mb-3 text-decoration-none d-flex align-items-center me-auto"
//         >
//           <i className="bi bi-arrow-left fs-7 me-1"></i> Back to Track Time
//         </button>

//         <div className="d-flex justify-content-between align-items-center mb-3">
//           <h6 className="fw-bolder text-dark fs-6 m-0" style={{ color: '#181C32' }}>
//             Enter Time Manually
//           </h6>
//           {(selectedClient || selectedProject || selectedTask || selectedCostCenter || selectedWorkType || hours || description) && (
//             <button
//               type="button"
//               onClick={handleClearAll}
//               className="btn btn-icon btn-active-light-danger btn-sm p-1 border-0"
//               title="Clear all dropdowns and fields"
//               style={{ width: '28px', height: '28px' }}
//             >
//               <i className="bi bi-arrow-counterclockwise fs-7 text-danger"></i>
//             </button>
//           )}
//         </div>

//         {isClientVisible && (
//           <div className="mb-3">
//             <label className="form-label fs-7 fw-bold text-dark mb-1">Client</label>
//             <Select
//               value={selectedClient}
//               onChange={handleClientChange}
//               options={clientOptions}
//               isLoading={loadingClients}
//               placeholder="Select Client"
//               styles={cleanDropdownStyles}
//               formatOptionLabel={(opt, { context }) => formatOptionWithIcon(opt, 'bi-building', context)}
//             />
//           </div>
//         )}

//         <div className="mb-3">
//           <label className="form-label fs-7 fw-bold text-dark mb-1">Project</label>
//           <Select
//             value={selectedProject}
//             onChange={handleProjectChange}
//             options={projectOptions}
//             isLoading={loadingProjects}
//             isDisabled={isClientVisible && !selectedClient}
//             placeholder="Select Project"
//             styles={cleanDropdownStyles}
//             formatOptionLabel={(opt, { context }) => formatOptionWithIcon(opt, 'bi-folder', context)}
//           />
//         </div>

//         <div className="mb-3">
//           <label className="form-label fs-7 fw-bold text-dark mb-1">Task</label>
//           <Select
//             value={selectedTask}
//             onChange={handleTaskChange}
//             options={taskOptions}
//             isLoading={loadingTasks}
//             isDisabled={!selectedProject}
//             placeholder="Select Task"
//             styles={cleanDropdownStyles}
//             formatOptionLabel={(opt, { context }) => formatOptionWithIcon(opt, 'bi-card-checklist', context)}
//           />
//         </div>

//         {isCostCenterVisible && (
//           <div className="mb-3">
//             <label className="form-label fs-7 fw-bold text-dark mb-1">Cost Center</label>
//             <Select
//               value={selectedCostCenter}
//               onChange={handleCostCenterChange}
//               options={costCenterOptions}
//               isLoading={loadingCostCenters}
//               isDisabled={!selectedTask}
//               placeholder="Select Cost Center"
//               styles={cleanDropdownStyles}
//               formatOptionLabel={(opt, { context }) => formatOptionWithIcon(opt, 'bi-diagram-3', context)}
//             />
//           </div>
//         )}

//         {isWorkTypeVisible && (
//           <div className="mb-3">
//             <label className="form-label fs-7 fw-bold text-dark mb-1">Work Type</label>
//             <Select
//               value={selectedWorkType}
//               onChange={handleWorkTypeChange}
//               options={workTypeOptions}
//               isLoading={loadingWorkTypes}
//               placeholder="Select Work Type"
//               styles={cleanDropdownStyles}
//               formatOptionLabel={(opt, { context }) => formatOptionWithIcon(opt, 'bi-briefcase', context)}
//             />
//           </div>
//         )}

//         <div className="mb-3">
//           <label className="form-label fs-7 fw-bold text-dark mb-1">Date</label>
//           <input
//             type="date"
//             className="form-control rounded-3 fw-semibold text-dark"
//             style={{ height: '38px', fontSize: '13px', borderColor: '#E4E6EF' }}
//             value={entryDate}
//             onChange={(e) => setEntryDate(e.target.value)}
//           />
//         </div>

//         {isClockPopoverActive ? (
//           <div className="mb-3 position-relative">
//             <label className="form-label fs-7 fw-bold text-dark mb-1">
//               Hours <span className="text-danger">*</span>
//             </label>
//             <div className="input-group">
//               <input
//                 type="text"
//                 readOnly
//                 className={`form-control bg-light cursor-pointer rounded-start-3 fw-semibold ${timeError ? 'is-invalid' : ''}`}
//                 style={{ height: '38px', fontSize: '13px', borderColor: '#E4E6EF' }}
//                 value={hours ? hours : ''}
//                 placeholder="Click to select start & end time"
//                 onClick={() => setShowTimePopover(true)}
//               />
//               <button
//                 className="btn btn-secondary border border-start-0 d-flex align-items-center justify-content-center px-3 rounded-end-3"
//                 type="button"
//                 style={{ height: '38px', borderColor: '#E4E6EF' }}
//                 onClick={() => setShowTimePopover(!showTimePopover)}
//               >
//                 <i className="bi bi-clock fs-7"></i>
//               </button>
//             </div>

//             {timeError && (
//               <div className="text-danger fs-9 mt-1 fw-semibold">
//                 {timeError}
//               </div>
//             )}

//             {showTimePopover && (
//               <ExtensionTimeEntryPopover
//                 initialStartTime={startTime}
//                 initialEndTime={endTime}
//                 onApply={(start, end, calculatedHours) => {
//                   setStartTime(start);
//                   setEndTime(end);
//                   setHours(calculatedHours);
//                   setTimeError('');
//                 }}
//                 onClose={() => setShowTimePopover(false)}
//               />
//             )}
//           </div>
//         ) : (
//           <div className="mb-3">
//             <label className="form-label fs-7 fw-bold text-dark mb-1">
//               Hours <span className="text-danger">*</span>
//             </label>
//             <input
//               type="text"
//               className={`form-control rounded-3 fw-semibold ${timeError ? 'is-invalid' : ''}`}
//               style={{ height: '38px', fontSize: '13px', borderColor: '#E4E6EF' }}
//               placeholder={decimalFormat ? '00.00' : '00:00'}
//               maxLength={5}
//               value={hours}
//               onChange={(e) => handleTimeChange(e, setHours)}
//               onBlur={handleTimeBlur}
//               onKeyDown={handleKeyDown}
//             />
//             {timeError && (
//               <div className="text-danger fs-9 mt-1 fw-semibold">
//                 {timeError}
//               </div>
//             )}
//           </div>
//         )}

//         <div className="mb-4">
//           <label className="form-label fs-7 fw-bold text-dark mb-1">
//             Description {isDescriptionRequired && <span className="text-danger"> *</span>}
//           </label>
//           <textarea
//             value={description}
//             onChange={(e) => {
//               setDescription(e.target.value.slice(0, 500));
//               if (descriptionError) setDescriptionError('');
//             }}
//             className={`form-control rounded-3 ${descriptionError ? 'is-invalid' : ''}`}
//             style={{ fontSize: '13px', resize: 'none', borderColor: '#E4E6EF' }}
//             rows={3}
//             placeholder="What did you work on?"
//           />
//           {descriptionError && (
//             <div className="text-danger fs-9 mt-1 fw-semibold">
//               {descriptionError}
//             </div>
//           )}
//         </div>
//       </div>

//       <div className="pt-2">
//         <button
//           type="button"
//           disabled={
//             !selectedTask ||
//             isSaving ||
//             Boolean(timeError) ||
//             !isHoursValid ||
//             Boolean(descriptionError)
//           }
//           onClick={handleSaveTimeEntry}
//           className="btn btn-primary btn-sm w-100 fs-6 fw-bold py-2"
//           style={{ backgroundColor: '#1B64F2', borderColor: '#1B64F2', borderRadius: '10px' }}
//         >
//           {isSaving ? 'Saving...' : 'Save Time Entry'}
//         </button>
//       </div>
//     </div>
//   );
// };

// export default ExtensionManualEntry;

// import React, { useState, useEffect } from 'react';
// import { useAppSelector } from '../../app/hooks';
// import Select from 'react-select';
// import { dropDownSelectionStyles } from '../../common/timeLiveFunction';
// import fetchAPI from '../../common/fetchAPI';
// import ExtensionTimeEntryPopover from './ExtensionTimeEntryPopover';

// declare const chrome: any;

// interface ExtensionManualEntryProps {
//   onBackToTimer: () => void;
// }

// export const ExtensionManualEntry: React.FC<ExtensionManualEntryProps> = ({ onBackToTimer }) => {
//   const {
//     sessionAccountEmployeeId,
//     timeZoneId,
//     showClientInTimesheet,
//     showCostCenterInTimeSheet,
//     showWorkTypeInTimeSheet,
//     IsTimeEntryDescriptionRequired,
//     showClockStartEnd,
//     decimalFormat, 
//   } = useAppSelector((state) => state.globalState);

//   const { defaultWorkTypeId } = useAppSelector((state) => state.timesheet);


//   const getCurrentFormattedTime = (offsetHours = 0): string => {
//     const now = new Date();
//     now.setHours(now.getHours() + offsetHours);
//     const hours = String(now.getHours()).padStart(2, '0');
//     const minutes = String(now.getMinutes()).padStart(2, '0');
//     return `${hours}:${minutes}`;
//   };

//   const showClientFlag: boolean = Boolean(showClientInTimesheet);
//   const showCostCenterFlag: boolean = Boolean(showCostCenterInTimeSheet);
//   const showWorkTypeFlag: boolean = Boolean(showWorkTypeInTimeSheet);
  

//   const isClockPopoverActive: boolean = Boolean(showClockStartEnd) && !Boolean(decimalFormat);

//   const [activeEmpId, setActiveEmpId] = useState<number | string | null>(null);
//   const [isClientVisible, setIsClientVisible] = useState<boolean>(showClientFlag);
//   const [isCostCenterVisible, setIsCostCenterVisible] = useState<boolean>(showCostCenterFlag);
//   const [isWorkTypeVisible, setIsWorkTypeVisible] = useState<boolean>(showWorkTypeFlag);

//   const [isStorageLoaded, setIsStorageLoaded] = useState<boolean>(false);

//   const [clientOptions, setClientOptions] = useState<{ value: any; label: string }[]>([]);
//   const [projectOptions, setProjectOptions] = useState<{ value: any; label: string }[]>([]);
//   const [taskOptions, setTaskOptions] = useState<{ value: any; label: string }[]>([]);
//   const [costCenterOptions, setCostCenterOptions] = useState<{ value: any; label: string }[]>([]);
//   const [workTypeOptions, setWorkTypeOptions] = useState<{ value: any; label: string }[]>([]);

//   const [selectedClient, setSelectedClient] = useState<any>(null);
//   const [selectedProject, setSelectedProject] = useState<any>(null);
//   const [selectedTask, setSelectedTask] = useState<any>(null);
//   const [selectedCostCenter, setSelectedCostCenter] = useState<any>(null);
//   const [selectedWorkType, setSelectedWorkType] = useState<any>(null);

//   const [timeError, setTimeError] = useState<string>('');
//   const [descriptionError, setDescriptionError] = useState<string>('');

//   const [savedIds, setSavedIds] = useState<{
//     clientId?: any;
//     projectId?: any;
//     taskId?: any;
//     costCenterId?: any;
//     workTypeId?: any;
//   }>({});

//   const [loadingClients, setLoadingClients] = useState(false);
//   const [loadingProjects, setLoadingProjects] = useState(false);
//   const [loadingTasks, setLoadingTasks] = useState(false);
//   const [loadingCostCenters, setLoadingCostCenters] = useState(false);
//   const [loadingWorkTypes, setLoadingWorkTypes] = useState(false);
//   const [isSaving, setIsSaving] = useState(false);

//   const [entryDate, setEntryDate] = useState<string>(new Date().toISOString().split('T')[0]);
  
//   const [hours, setHours] = useState<string>('');
//   const [startTime, setStartTime] = useState<string>(() => getCurrentFormattedTime(0));
//   const [endTime, setEndTime] = useState<string>(() => getCurrentFormattedTime(0));
//   const [showTimePopover, setShowTimePopover] = useState<boolean>(false);
//   const [description, setDescription] = useState<string>('');
//   const [isDescriptionRequired, setIsDescriptionRequired] = useState<boolean>(Boolean(IsTimeEntryDescriptionRequired));

//   const persistStorage = (dataToSave: Record<string, any>) => {
//     if (typeof chrome !== 'undefined' && chrome.storage) {
//       chrome.storage.local.set(dataToSave);
//     } else {
//       Object.entries(dataToSave).forEach(([k, v]) => {
//         if (v === null || v === undefined) localStorage.removeItem(k);
//         else localStorage.setItem(k, JSON.stringify(v));
//       });
//     }
//   };

//   useEffect(() => {
//     const keysToFetch = [
//       'sessionAccountEmployeeId',
//       'accountEmployeeId',
//       'showClientInTimesheet',
//       'showCostCenterInTimeSheet',
//       'showWorkTypeInTimeSheet',
//       'manual_clientId',
//       'manual_projectId',
//       'manual_taskId',
//       'manual_costCenterId',
//       'manual_workTypeId',
//       'IsTimeEntryDescriptionRequired',
//     ];

//     if (typeof chrome !== 'undefined' && chrome.storage) {
//       chrome.storage.local.get(keysToFetch, (result: any) => {
//         const resolvedEmpId = result?.accountEmployeeId || result?.sessionAccountEmployeeId || sessionAccountEmployeeId;
//         if (resolvedEmpId) setActiveEmpId(Number(resolvedEmpId));

//         if (result?.showClientInTimesheet !== undefined) setIsClientVisible(Boolean(result.showClientInTimesheet));
//         if (result?.showCostCenterInTimeSheet !== undefined) setIsCostCenterVisible(Boolean(result.showCostCenterInTimeSheet));
//         if (result?.showWorkTypeInTimeSheet !== undefined) setIsWorkTypeVisible(Boolean(result.showWorkTypeInTimeSheet));
//         if (result?.IsTimeEntryDescriptionRequired !== undefined) {
//         setIsDescriptionRequired(Boolean(result.IsTimeEntryDescriptionRequired));
//       } else if (IsTimeEntryDescriptionRequired !== undefined && IsTimeEntryDescriptionRequired !== null) {
//         setIsDescriptionRequired(Boolean(IsTimeEntryDescriptionRequired));
//         persistStorage({ IsTimeEntryDescriptionRequired: Boolean(IsTimeEntryDescriptionRequired) });
//       }

//         setSavedIds({
//           clientId: result?.manual_clientId ?? null,
//           projectId: result?.manual_projectId ?? null,
//           taskId: result?.manual_taskId ?? null,
//           costCenterId: result?.manual_costCenterId ?? null,
//           workTypeId: result?.manual_workTypeId ?? null,
//         });

//         setIsStorageLoaded(true);
//       });
//     } else {
//       if (sessionAccountEmployeeId) setActiveEmpId(Number(sessionAccountEmployeeId));
//       setIsDescriptionRequired(Boolean(IsTimeEntryDescriptionRequired));
//       const parseLocal = (k: string) => {
//         const val = localStorage.getItem(k);
//         return val ? JSON.parse(val) : null;
//       };
//       setSavedIds({
//         clientId: parseLocal('manual_clientId'),
//         projectId: parseLocal('manual_projectId'),
//         taskId: parseLocal('manual_taskId'),
//         costCenterId: parseLocal('manual_costCenterId'),
//         workTypeId: parseLocal('manual_workTypeId'),
//       });
//       setIsStorageLoaded(true);
//     }
//   }, [sessionAccountEmployeeId,IsTimeEntryDescriptionRequired]);

//   const handleClearAll = () => {
//     setSelectedClient(null);
//     setSelectedProject(null);
//     setSelectedTask(null);
//     setSelectedCostCenter(null);
//     setSelectedWorkType(null);
//     setHours('');
//     setTimeError('');
//     setDescription('');
//     setSavedIds({});
//     setDescriptionError('');

//     persistStorage({
//       manual_clientId: null,
//       manual_projectId: null,
//       manual_taskId: null,
//       manual_costCenterId: null,
//       manual_workTypeId: null,
//     });
//   };

//   const handleClientChange = (opt: any) => {
//     setSelectedClient(opt);
//     const val = opt?.value ?? null;
//     setSavedIds((prev) => ({ ...prev, clientId: val, projectId: null, taskId: null, costCenterId: null }));
//     persistStorage({
//       manual_clientId: val,
//       manual_projectId: null,
//       manual_taskId: null,
//       manual_costCenterId: null,
//     });

//     if (!opt) {
//       setSelectedProject(null);
//       setSelectedTask(null);
//       setSelectedCostCenter(null);
//     }
//   };

//   const handleProjectChange = (opt: any) => {
//     setSelectedProject(opt);
//     const val = opt?.value ?? null;
//     setSavedIds((prev) => ({ ...prev, projectId: val, taskId: null, costCenterId: null }));
//     persistStorage({
//       manual_projectId: val,
//       manual_taskId: null,
//       manual_costCenterId: null,
//     });

//     if (!opt) {
//       setSelectedTask(null);
//       setSelectedCostCenter(null);
//     }
//   };

//   const handleTaskChange = (opt: any) => {
//     setSelectedTask(opt);
//     const val = opt?.value ?? null;
//     setSavedIds((prev) => ({ ...prev, taskId: val, costCenterId: null }));
//     persistStorage({
//       manual_taskId: val,
//       manual_costCenterId: null,
//     });

//     if (!opt) {
//       setSelectedCostCenter(null);
//     }
//   };

//   const handleCostCenterChange = (opt: any) => {
//     setSelectedCostCenter(opt);
//     const val = opt?.value ?? null;
//     setSavedIds((prev) => ({ ...prev, costCenterId: val }));
//     persistStorage({ manual_costCenterId: val });
//   };

//   const handleWorkTypeChange = (opt: any) => {
//     setSelectedWorkType(opt);
//     const val = opt?.value ?? null;
//     setSavedIds((prev) => ({ ...prev, workTypeId: val }));
//     persistStorage({ manual_workTypeId: val });
//   };

//   useEffect(() => {
//     if (!isStorageLoaded || !activeEmpId) return;

//     if (isClientVisible) {
//       setLoadingClients(true);
//       fetchAPI(`api/Timesheets/GetAccountClientForTimesheet/${activeEmpId}/0`, 'GET', '', true)
//         .then((res) => res.json())
//         .then((data) => {
//           const list = Array.isArray(data) ? data : data?.data || [];
//           const formatted = list.map((item: any) => ({
//             value: item.AccountClientId,
//             label: item.ClientName,
//           }));
//           setClientOptions(formatted);

//           const targetId = savedIds.clientId;
//           if (targetId) {
//             const match = formatted.find((c: any) => c.value === targetId);
//             if (match) setSelectedClient(match);
//           }
//         })
//         .catch((err) => console.error('Error fetching clients:', err))
//         .finally(() => setLoadingClients(false));
//     } else {
//       setLoadingProjects(true);
//       fetchAPI(`api/Projects/GetAccountProjectsForTimesheet/0/${activeEmpId}`, 'GET', '', true)
//         .then((res) => res.json())
//         .then((data) => {
//           const list = Array.isArray(data) ? data : data?.data || [];
//           const formatted = list.map((item: any) => ({
//             value: item.AccountProjectId,
//             label: item.ProjectName,
//           }));
//           setProjectOptions(formatted);

//           const targetId = savedIds.projectId;
//           if (targetId) {
//             const match = formatted.find((p: any) => p.value === targetId);
//             if (match) setSelectedProject(match);
//           }
//         })
//         .catch((err) => console.error('Error fetching projects:', err))
//         .finally(() => setLoadingProjects(false));
//     }

//     if (isWorkTypeVisible) {
//       setLoadingWorkTypes(true);
//       fetchAPI('api/WorkTypes', 'GET', '', true)
//         .then((res) => res.json())
//         .then((data) => {
//           const list = Array.isArray(data) ? data : data?.data || [];
//           const formatted = list.map((item: any) => ({
//             value: item.AccountWorkTypeId,
//             label: item.AccountWorkType,
//           }));
//           setWorkTypeOptions(formatted);

//           const targetId = savedIds.workTypeId;
//           if (targetId) {
//             const match = formatted.find((w: any) => w.value === targetId);
//             if (match) setSelectedWorkType(match);
//           }
//         })
//         .catch((err) => console.error('Error fetching work types:', err))
//         .finally(() => setLoadingWorkTypes(false));
//     }
//   }, [isStorageLoaded, activeEmpId, isClientVisible, isWorkTypeVisible]);

//   useEffect(() => {
//     if (!isClientVisible || !selectedClient?.value || !activeEmpId) {
//       if (isClientVisible && !selectedClient) setProjectOptions([]);
//       return;
//     }

//     setLoadingProjects(true);
//     fetchAPI(`api/Projects/GetAccountProjectsForTimesheet/${selectedClient.value}/${activeEmpId}`, 'GET', '', true)
//       .then((res) => res.json())
//       .then((data) => {
//         const list = Array.isArray(data) ? data : data?.data || [];
//         const formatted = list.map((item: any) => ({
//           value: item.AccountProjectId,
//           label: item.ProjectName,
//         }));
//         setProjectOptions(formatted);

//         const targetId = savedIds.projectId;
//         if (targetId) {
//           const match = formatted.find((p: any) => p.value === targetId);
//           if (match) setSelectedProject(match);
//         }
//       })
//       .catch((err) => console.error('Error fetching projects:', err))
//       .finally(() => setLoadingProjects(false));
//   }, [selectedClient, activeEmpId, isClientVisible]);

//   useEffect(() => {
//     if (!selectedProject?.value || !activeEmpId) {
//       setTaskOptions([]);
//       return;
//     }

//     setLoadingTasks(true);
//     fetchAPI(`api/Timesheets/GetAccountProjectTasksForTimesheet/${selectedProject.value}/${activeEmpId}`, 'GET', '', true)
//       .then((res) => res.json())
//       .then((data) => {
//         const list = Array.isArray(data) ? data : data?.data || [];
//         const formatted = list.map((item: any) => ({
//           value: item.AccountProjectTaskId,
//           label: item.TaskName,
//         }));
//         setTaskOptions(formatted);

//         const targetId = savedIds.taskId;
//         if (targetId) {
//           const match = formatted.find((t: any) => t.value === targetId);
//           if (match) setSelectedTask(match);
//         }
//       })
//       .catch((err) => console.error('Error fetching tasks:', err))
//       .finally(() => setLoadingTasks(false));
//   }, [selectedProject, activeEmpId]);

//   useEffect(() => {
//     if (!isCostCenterVisible || !selectedTask?.value || !selectedProject?.value || !activeEmpId) {
//       setCostCenterOptions([]);
//       return;
//     }

//     setLoadingCostCenters(true);
//     fetchAPI(`api/v2/CostCenter/GetAccountCostCenterForTimesheet/${activeEmpId}/${selectedProject.value}/${selectedTask.value}`, 'GET', '', true)
//       .then((res) => res.json())
//       .then((data) => {
//         const list = Array.isArray(data) ? data : data?.data || [];
//         const formatted = list.map((item: any) => ({
//           value: item.AccountCostCenterId,
//           label: item.AccountCostCenter,
//         }));
//         setCostCenterOptions(formatted);

//         const targetId = savedIds.costCenterId;
//         if (targetId) {
//           const match = formatted.find((c: any) => c.value === targetId);
//           if (match) setSelectedCostCenter(match);
//         }
//       })
//       .catch((err) => console.error('Error fetching cost centers:', err))
//       .finally(() => setLoadingCostCenters(false));
//   }, [selectedTask, selectedProject, activeEmpId, isCostCenterVisible]);

//   const convertTimeToDecimal = (timeStr: string): number => {
//     if (!timeStr) return 0;

//     if (decimalFormat) {
//       const separator = timeStr.includes('.') ? '.' : ':';
//       const [h, m] = timeStr.split(separator).map(Number);
//       return Number(((h || 0) + (m || 0) / 100).toFixed(2));
//     }

//     if (timeStr.includes(':')) {
//       const [h, m] = timeStr.split(':').map(Number);
//       return Number(((h || 0) + (m || 0) / 60).toFixed(2));
//     }

//     const val = parseFloat(timeStr);
//     return isNaN(val) ? 0 : val;
//   };

//   const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>, setHours: (val: string) => void) => {
//     let value = e.target.value.replace(/\D/g, '');
//     if (value.length > 4) value = value.slice(0, 4);

//     const separator = decimalFormat ? '.' : ':';
//     const maxMinutes = decimalFormat ? 99 : 59;

//     let formattedValue = value;
//     if (value.length >= 3) {
//       formattedValue = `${value.slice(0, 2)}${separator}${value.slice(2)}`;
//     }

//     setHours(formattedValue);

//     if (formattedValue.length === 5) {
//       const [h, m] = formattedValue.split(separator).map(Number);

//       if (h >= 24) {
//         setTimeError('Invalid Time! Hours must be less than 24');
//       } else if (m > maxMinutes) {
//         setTimeError(`Invalid Time! Minutes must not exceed ${maxMinutes}`);
//       } else {
//         setTimeError('');
//       }
//     } else if (formattedValue.length <= 2 && formattedValue !== '') {
//       const h = Number(formattedValue);
//       if (h >= 24) {
//         setTimeError('Invalid Time! Hours must be less than 24');
//       } else {
//         setTimeError('');
//       }
//     } else {
//       setTimeError('');
//     }
//   };

//   const handleTimeBlur = () => {
//     if (!hours) return;

//     const separator = decimalFormat ? '.' : ':';
//     const maxMinutes = decimalFormat ? 99 : 59;

//     let formatted = hours;

//     if (!hours.includes(separator)) {
//       if (hours.length === 1) formatted = `0${hours}${separator}00`;
//       else if (hours.length === 2) formatted = `${hours}${separator}00`;
//       setHours(formatted);
//     }

//     if (formatted.includes(separator)) {
//       const [h, m] = formatted.split(separator).map(Number);
//       if (h >= 24) {
//         setTimeError('Invalid Time! Hours must be less than 24');
//       } else if (m > maxMinutes) {
//         setTimeError(`Invalid Time! Minutes must not exceed ${maxMinutes}`);
//       } else {
//         setTimeError('');
//       }
//     }
//   };

//   const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
//     const allowedKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete'];
//     if (allowedKeys.includes(e.key)) return;
//     if (!/^\d$/.test(e.key)) e.preventDefault();
//   };

//   const handleSaveTimeEntry = async () => {
//     setTimeError('');
//     setDescriptionError('');

//     if (!selectedTask || !selectedTask.value) {
//       alert('Please select a Task.');
//       return;
//     }

//     const hoursDecimal = convertTimeToDecimal(hours);
//     if (!hours || hours.trim() === '' || hours === '00:00' || hours === '00.00' || hours === '__:__' || hoursDecimal <= 0) {
//       setTimeError('Please enter valid hours (minimum 1 minute).');
//       return;
//     }

//     if (timeError) {
//       return;
//     }

//     if (isDescriptionRequired && !description.trim()) {
//       setDescriptionError('Description is required.');
//       return;
//     }

//     setIsSaving(true);
//     try {
//       let periodId = 0;
//       let periodStartDate = entryDate;
//       let periodEndDate = entryDate;
//       let timesheetPeriodType = 'Weekly';

//       try {
//         const periodRes = await fetchAPI(`api/Timesheets/GetTimesheetPeriodForReact/${activeEmpId}/${entryDate}`, 'GET', '', true);
//         if (periodRes.status === 200) {
//           const periodJson = await periodRes.json();
//           const targetData = periodJson?.data || periodJson;
//           const periodObj = targetData?.AccountEmployeeTimeEntryPeriod || {};

//           periodId = periodObj?.AccountEmployeeTimeEntryPeriodId || null;
//           periodStartDate = periodObj?.TimeEntryStartDate || entryDate;
//           periodEndDate = periodObj?.TimeEntryEndDate || entryDate;
//           timesheetPeriodType = periodObj?.TimeEntryViewType || 'Weekly';
//         }
//       } catch (periodErr) {
//         console.warn('Could not fetch period info, proceeding with defaults:', periodErr);
//       }

//       const postObject = {
//         AccountEmployeeTimeEntryPeriodId: periodId,
//         AccountEmployeeTimeEntryApprovalProjectId: null,
//         AccountEmployeeTimeEntryId: 0,
//         AccountEmployeeId: Number(activeEmpId),
//         AccountClientId: isClientVisible ? selectedClient?.value || 0 : 0,
//         AccountProjectId: selectedProject?.value || 0,
//         AccountProjectTaskId: selectedTask?.value || 0,
//         AccountCostCenterId: isCostCenterVisible ? selectedCostCenter?.value || 0 : 0,
//         AccountWorkTypeId: isWorkTypeVisible ? selectedWorkType?.value || defaultWorkTypeId || 0 : defaultWorkTypeId || 0,
//         AccountTimeOffTypeId: null,
//         Hours: hoursDecimal,
//         Percentage: null,
//         PeriodStartDate: periodStartDate,
//         PeriodEndDate: periodEndDate,
//         TimeEntryDate: entryDate,
//         StartTime: isClockPopoverActive && startTime ? `${entryDate}T${startTime}:00` : null,
//         EndTime: isClockPopoverActive && endTime ? `${entryDate}T${endTime}:00` : null,
//         IsTimeOff: false,
//         TimesheetPeriodType: timesheetPeriodType,
//         IsSubmitted: false,
//         Description: description,
//         Reason: '',
//         TimeZoneId: timeZoneId,
//         showClockStartEnd: isClockPopoverActive,
//         SystemPlatformSourceId: '50E254BE-D515-4FBA-BFB7-DFD27852D023',
//       };

//       const response = await fetchAPI('api/Timesheets/AddAccountEmployeeTimeEntry', 'POST', postObject, true);
//       if (response.ok) {
//         alert('Time Entry Added Successfully!');
//         setHours('');
//         setDescription('');
//         onBackToTimer();
//       } else {
//         const errData = await response.json().catch(() => null);
//         alert(errData?.message || 'Failed to save time entry.');
//       }
//     } catch (error) {
//       console.error('Error saving time entry:', error);
//       alert('An error occurred while saving.');
//     } finally {
//       setIsSaving(false);
//     }
//   };

//   const isHoursValid = Boolean(hours) && hours !== '00:00' && hours !== '00.00' && hours !== '__:__' && convertTimeToDecimal(hours) > 0;

//   return (
//     <div className="d-flex flex-column">
//       <button
//         type="button"
//         onClick={onBackToTimer}
//         className="btn btn-link btn-color-primary p-0 fs-8 fw-bold mb-3 text-decoration-none d-flex align-items-center me-auto"
//       >
//         <i className="bi bi-arrow-left fs-7 me-1"></i> Back to Track Time
//       </button>

//       <div className="d-flex justify-content-between align-items-center mb-3">
//         <h6 className="fw-bolder text-gray-900 fs-7 m-0">Enter Time Manually</h6>
//         {(selectedClient || selectedProject || selectedTask || selectedCostCenter || selectedWorkType || hours || description) && (
//           <button
//             type="button"
//             onClick={handleClearAll}
//             className="btn btn-icon btn-active-light-danger btn-sm p-1 border-0"
//             title="Clear all dropdowns and fields"
//             style={{ width: '28px', height: '28px' }}
//           >
//             <i className="bi bi-arrow-counterclockwise fs-7 text-danger"></i>
//           </button>
//         )}
//       </div>

//       {isClientVisible && (
//         <div className="mb-3">
//           <label className="form-label fs-8 fw-semibold text-gray-600 mb-1">Client</label>
//           <Select
//             value={selectedClient}
//             onChange={handleClientChange}
//             options={clientOptions}
//             isLoading={loadingClients}
//             placeholder="Select Client"
//             styles={dropDownSelectionStyles}
//           />
//         </div>
//       )}

//       <div className="mb-3">
//         <label className="form-label fs-8 fw-semibold text-gray-600 mb-1">Project</label>
//         <Select
//           value={selectedProject}
//           onChange={handleProjectChange}
//           options={projectOptions}
//           isLoading={loadingProjects}
//           isDisabled={isClientVisible && !selectedClient}
//           placeholder="Select Project"
//           styles={dropDownSelectionStyles}
//         />
//       </div>

//       <div className="mb-3">
//         <label className="form-label fs-8 fw-semibold text-gray-600 mb-1">Task</label>
//         <Select
//           value={selectedTask}
//           onChange={handleTaskChange}
//           options={taskOptions}
//           isLoading={loadingTasks}
//           isDisabled={!selectedProject}
//           placeholder="Select Task"
//           styles={dropDownSelectionStyles}
//         />
//       </div>

//       {isCostCenterVisible && (
//         <div className="mb-3">
//           <label className="form-label fs-8 fw-semibold text-gray-600 mb-1">Cost Center</label>
//           <Select
//             value={selectedCostCenter}
//             onChange={handleCostCenterChange}
//             options={costCenterOptions}
//             isLoading={loadingCostCenters}
//             isDisabled={!selectedTask}
//             placeholder="Select Cost Center"
//             styles={dropDownSelectionStyles}
//           />
//         </div>
//       )}

//       {isWorkTypeVisible && (
//         <div className="mb-3">
//           <label className="form-label fs-8 fw-semibold text-gray-600 mb-1">Work Type</label>
//           <Select
//             value={selectedWorkType}
//             onChange={handleWorkTypeChange}
//             options={workTypeOptions}
//             isLoading={loadingWorkTypes}
//             placeholder="Select Work Type"
//             styles={dropDownSelectionStyles}
//           />
//         </div>
//       )}

//       <div className="mb-3">
//         <label className="form-label fs-8 fw-semibold text-gray-600 mb-1">Date</label>
//         <input
//           type="date"
//           className="form-control"
//           style={{ height: '38px', fontSize: '13px' }}
//           value={entryDate}
//           onChange={(e) => setEntryDate(e.target.value)}
//         />
//       </div>

//       {/* Dynamic Condition Block */}
//       {isClockPopoverActive ? (
//        <div className="mb-3 position-relative">
//   <label className="form-label fs-8 fw-semibold text-gray-600 mb-1">
//     Hours <span className="text-danger">*</span>
//   </label>
//   <div className="input-group">
//     <input
//       type="text"
//       readOnly
//       className={`form-control bg-light cursor-pointer ${timeError ? 'is-invalid' : ''}`}
//       style={{ height: '38px', fontSize: '13px' }}
//       value={hours ? hours : ''}
//       placeholder="Click to select start & end time"
//       onClick={() => setShowTimePopover(true)}
//     />
//     <button
//       className="btn btn-secondary border d-flex align-items-center justify-content-center px-3"
//       type="button"
//       style={{ height: '38px' }}
//       onClick={() => setShowTimePopover(!showTimePopover)}
//     >
//       <i className="bi bi-clock fs-7"></i>
//     </button>
//   </div>

//   {timeError && (
//     <div className="text-danger fs-9 mt-1 fw-semibold">
//       {timeError}
//     </div>
//   )}

//   {showTimePopover && (
//     <ExtensionTimeEntryPopover
//       initialStartTime={startTime}
//       initialEndTime={endTime}
//       onApply={(start, end, calculatedHours) => {
//         setStartTime(start);
//         setEndTime(end);
//         setHours(calculatedHours);
//         setTimeError('');
//       }}
//       onClose={() => setShowTimePopover(false)}
//     />
//   )}
// </div>
//       ) : (
//         <div className="mb-3">
//           <label className="form-label fs-8 fw-semibold text-gray-600 mb-1">Hours  <span className="text-danger">*</span></label>
//           <input
//             type="text"
//             className={`form-control ${timeError ? 'is-invalid' : ''}`}
//             style={{ height: '38px', fontSize: '13px' }}
//             placeholder={decimalFormat ? '00.00' : '00:00'}
//             maxLength={5}
//             value={hours}
//             onChange={(e) => handleTimeChange(e, setHours)}
//             onBlur={handleTimeBlur}
//             onKeyDown={handleKeyDown}
//           />
//           {timeError && (
//             <div className="text-danger fs-9 mt-1 fw-semibold">
//               {timeError}
//             </div>
//           )}
//         </div>
//       )}

//       <div className="mb-4">
//         <div className="d-flex justify-content-between align-items-center mb-1">
//           <label className="form-label fs-8 fw-semibold text-gray-600 m-0">
//             Description {isDescriptionRequired && <span className="text-danger"> *</span>}
//           </label>
//         </div>
//         <textarea
//           value={description}
//           onChange={(e) => {
//             setDescription(e.target.value.slice(0, 500));
//             if (descriptionError) setDescriptionError('');
//           }}
//           className={`form-control ${descriptionError ? 'is-invalid' : ''}`}
//           style={{ fontSize: '13px' }}
//           rows={3}
//           placeholder="What did you work on?"
//         />
//         {descriptionError && (
//           <div className="text-danger fs-9 mt-1 fw-semibold">
//             {descriptionError}
//           </div>
//         )}
//       </div>

//       <button
//         type="button"
//         disabled={
//           !selectedTask ||
//           isSaving ||
//           Boolean(timeError) ||
//           !isHoursValid ||
//           Boolean(descriptionError)
//         }
//         onClick={handleSaveTimeEntry}
//         className="btn btn-extension-primary btn-sm w-100 fs-7 fw-bold py-2 mb-2"
//       >
//         {isSaving ? 'Saving...' : 'Save Time Entry'}
//       </button>
//     </div>
//   );
// };

// export default ExtensionManualEntry;